<template>
    <v-container class="py-5 px-5" fluid v-if="this.$store.state.signedIn">

        <v-layout justify-center wrap>
            <!-- start of org rules -->
            <v-flex xs12 md10 class="py-2 px-2">
                <!-- Toolbar -->
                <v-toolbar dark flat color="secondary">
                    <v-toolbar-title @click="showOrgASNs=!showOrgASNs" :style="$store.state.textFormatting.headers"> {{organisation}} Rules </v-toolbar-title>
                    <v-progress-circular v-if="loading" indeterminate color="white"></v-progress-circular>
                    <v-spacer></v-spacer>
                    <v-flex shrink align-center justify-center>
                        <br />
                        <v-switch v-model="showAdvanced" @change="advancedChange()">
                            <template v-slot:label>
                                <div :style="$store.state.textFormatting.main"><b>Advanced settings</b></div>
                            </template>
                        </v-switch>
                    </v-flex>
                    <downloadcsv
                        class   = "btn btn-default"
                        :data   = "organisation_download_rules"
                        name = "orgRules.csv">
                        <v-tooltip top eager color="primary" id="exit-app">
                            <template v-slot:activator="{ on }">
                                <v-btn small dark v-on="on" color="success" :style="$store.state.textFormatting.main"> <v-icon>save_alt</v-icon> Export Org Rules </v-btn>
                            </template>
                            <span :style="$store.state.textFormatting.main">CSV of organisation Rules</span>
                        </v-tooltip>        
                    </downloadcsv>
                    <v-btn v-if="user_type!='Analyst'" @click="clickRule(null,'org','add')" color="success" :style="$store.state.textFormatting.main"> Add Rule <v-icon>add</v-icon> </v-btn>
                    <v-btn 
                        v-if="user_type=='Analyst'" 
                        disabled 
                        color="success" 
                        :style="$store.state.textFormatting.main"
                        > Add Rule <v-icon>add</v-icon> 
                    </v-btn>
                    <info v-if="user_type=='Analyst'" >
                      <template slot>
                        Role restriction in place
                      </template>
                      <template slot="body">
                        The Add Rule action for the Organisation rules is disabled due to restrictions on your role. Only Developers and Admins have the ability to add/update Organisation-level rules.<br/>
                        Your role is [{{ user_type }}].
                      </template>
                    </info>
                    <info>
                        <template slot>
                            Add an Alert Rule to your organisation, or remove/ edit existing rule.
                        </template>
                         <template slot="body">
                            The alerting mechanism works in the following way: <br />
                            
                            <p> <h2> STANDARD ALERTING (no advanced settings) </h2> </p>
                            <ul>
                                <li> A user can define a Rule based on an ASN or Prefix, give it a name (required), and will be alerted upon <b>ownership assertions</b>, <b>Path Changes</b> and/or <b>First Seen Prefixes</b> where the Prefix(es) and ASN(s) are matched in observed BGP messages. </li>
                                <li> Users can specify <b>which event types to be alerted upon</b>, of which there are 3 in total: 
                                    <ul>
                                        <li><b>Ownership Assertions</b></li>
                                        <li><b>Path Changes</b></li>
                                        <li><b>First Seens</b></li>
                                    </ul>
                                    There are descriptions of each of these in <b>hover over panels throughout the page - hover your cursor where you see any of these terms</b>
                                </li>
                            </ul>

                            <ul>
                                <li> The user can also decide whether to receive emails from these alerts, again split out by the event type </li>
                                <li> Hijack and Impact score thresholds can also be set, which can further refine down the amount of alerts you will receive. </li>
                                <li>
                                    The scores of the <b> event </b> are compared with the <b> thresholds </b>. If hijack <b> and/or (set by the user for each ASN - default AND) </b>
                                    impact scores are <b> greater than or equal to </b> the given thresholds, a notification/ alert is created in the mySQL table. There are some predefined thresholds:
                                    <ul>
                                        <li> <b> Regular: H = 30, I = 10, V = 5 </b> for when you are interested in some potentially dangerous anomalies, but not the less dangerous ones </li>
                                        <li> <b> High Importance: H = 20, I = 8, V = 2 </b> for when you want to know about most anomalies, but not those we deem very unlikely to be hijack events </li>
                                        <li> <b> Low Importance: H = 50, I = 25, V = 10 </b> for when you only need to see the very high risk anomalies </li>
                                        <li> <b> Show All: H = 0, I = 0, V = 0 (default)</b> will trigger alerts for all incoming events </li>
                                    </ul>
                                </li>
                                
                                <li> When an alert occurs with emails turned on, the following occurs:
                                    <ul>
                                        <li> An initial email is sent out, alerting the user of the alert beginning </li>
                                        <li> When additional events occur with the same basic data (in the same 5 min window), and the total 
                                            passes the <b>Repeat Email Alert Threshold - set by the user</b>, an additional email is sent out </li>
                                        <li> This continues until the 5 minute window elapses, at which point a new notification / alert will 
                                            begin </li>
                                    </ul>
                                </li>
                                <li> <b>Lastly</b>, the user may add multiple ASNs to an <b>AS Group</b>. ASNs in a common group behave in one of the following, user defined, ways:
                                    <ul>
                                        <li><b>EXCLUDE:</b> Ownership assertions between ASNs in the group will <b>not create alerts</b> e.g. if I have AS 1 and AS 2 in a group and AS 2 asserts ownership over one of AS 1's prefixes, I will not be alerted </li>
                                        <li><b>HIJACK:</b> Ownership assertions between ASNs in the group will have their hijack scores overriden and set to the user defined score </li>
                                    </ul>
                                </li>
                            </ul>

                            <p> <h2> ADVANCED ALERTING (advanced settings) </h2> </p>
                            <ul>
                                <li> A user can define score overrides for a given rule to raise the immportance of an alert. These scores are visible only to the Organisation or User.</li>
                            </ul>
                            <ul>
                                <li> A user can also define more detailed Prefix, Origin and Path based rules as follows:: 
                                    <ul>
                                        <li><b>Additional "Except" claused for Prefix, Origin and Path</b> - these allow the rules to be fine tuned to exclude alerts based on specific criteria. e.g. alert on ownership assertions for prefix 1.1.1.0/24 except where ASN is 1234</li>
                                        <li><b>Optional Origin Alerting Conditions</b> - where an origin is normally triggered as a "new" or "old" origin, rules can be tuned to be specific about the direction of change. e.g. alert on ownership assertions for prefix 1.1.1.90/24 only when ASN 1234 is the new origin</li>
                                        <li><b>Path Position</b> - this allows users to trigger alerts where an ASN is found "anywhere" in the path or at the "origin + 1" postion</li>
                                    </ul>
                                </li>
                            </ul>
                            <br />
                            <b> NOTE: Updates to thresholds do not work retrospectively, so will not alter previously created alerts and alert counts. There is also a delay in the analytics engine picking up the changes, so you may continue to receive alerts after making changes. </b>
                        </template>
                    </info>
                </v-toolbar>  
                <!-- end of toolbar -->

                <!-- start of ORG rules -->
                <v-card v-if="showOrgASNs">  
                    <!-- data table -->
                    <v-data-table :headers="alertHeaders" :items="orgRules" :key="id"
                            :rows-per-page-items="rowsPerPageItems" :pagination.sync="pagination" > 
                            <template slot="headerCell" slot-scope="props">
                                <v-tooltip top eager>
                                <span slot="activator" :style="$store.state.textFormatting.main">
                                    {{ props.header.text }}
                                </span>
                                <span :style="$store.state.textFormatting.main">
                                    {{ props.header.ad_text }}
                                </span>
                                </v-tooltip>
                            </template>   
                            <template slot="items" slot-scope="props">
                                <tr>
                                    <td class="text-xs-center" :style="$store.state.textFormatting.main"> {{ props.item.alertgroup }} </td>
                                    <td class="text-xs-center" :style="$store.state.textFormatting.main"> {{ props.item.alertname }} </td>
                                    <td class="text-xs-center" :style="$store.state.textFormatting.main"> <div style="text-align:right">
                                        Assertions : <v-icon v-if="props.item.alerts.assertion"> check </v-icon><v-icon v-if="!props.item.alerts.assertion"> clear </v-icon>(<v-icon v-if="props.item.emails.assertion"> check </v-icon><v-icon v-if="!props.item.emails.assertion"> clear </v-icon>)<br >
                                        Path Changes :  <v-icon v-if="props.item.alerts.pathchange"> check </v-icon><v-icon v-if="!props.item.alerts.pathchange"> clear </v-icon>(<v-icon v-if="props.item.emails.pathchange"> check </v-icon><v-icon v-if="!props.item.emails.pathchange"> clear </v-icon>)<br >
                                        First Seen :  <v-icon v-if="props.item.alerts.firstseen"> check </v-icon><v-icon v-if="!props.item.alerts.firstseen"> clear </v-icon>(<v-icon v-if="props.item.emails.firstseen"> check </v-icon><v-icon v-if="!props.item.emails.firstseen"> clear </v-icon>)<br ></div>
                                         </td>
                                    <td class="text-xs-center" :style="$store.state.textFormatting.main">
                                        <span v-for="origin in props.item.filter.origin.is">{{ origin }}<br /></span></td>
                                    <td class="text-xs-center" :style="$store.state.textFormatting.main">
                                        <span v-for="prefix in props.item.filter.prefix.is">{{ prefix }}<br /></span></td>
                                    <td class="text-xs-center" :style="$store.state.textFormatting.main">
                                        <span v-for="path in props.item.filter.path.contains">{{ path }}<br /></span></td>

                                    <td class="text-xs-center" :style="$store.state.textFormatting.main" v-if="showAdvanced"> {{ props.item.filter.scores.hijack }}</td>
                                    
                                    <td class="text-xs-center" :style="$store.state.textFormatting.main" v-if="showAdvanced"> {{ props.item.filter.scores.andor }}</td>
                                    <td class="text-xs-center" :style="$store.state.textFormatting.main" v-if="showAdvanced"> {{ props.item.filter.scores.impact }}</td>
                                    <td class="text-xs-center" :style="$store.state.textFormatting.main" v-if="showAdvanced"> {{ props.item.emails.repeat }}</td>

                                    <td class="text-xs-center" :style="$store.state.textFormatting.main"> {{ props.item.notification_count }} </td>
                                    <td class="text-xs-center">
                                        
                                        <v-tooltip top eager close-delay=100>
                                            <template  v-slot:activator="{ on }">
                                                <v-icon v-on="on" small class="mr-2" @click="clickRule(props.item,'org','edit')" :style="$store.state.textFormatting.main"> edit </v-icon>
                                            </template>
                                            <span :style="$store.state.textFormatting.main">Edit Rule</span>
                                        </v-tooltip>
                                        
                                        <v-tooltip v-if="user_type!='Analyst'" top eager close-delay=100>
                                            <template  v-slot:activator="{ on }">
                                                <v-icon v-on="on" small class="mr-2" @click="prepRemoveRule(props.item,'org')" :style="$store.state.textFormatting.main"> delete </v-icon>
                                            </template>
                                            <span :style="$store.state.textFormatting.main">Remove Rule</span>
                                        </v-tooltip>                                        
                                    </td>
                                    <td class="text-xs-center">
                                          <v-tooltip  v-if="user_type!='Analyst'" top eager close-delay=100>
                                            <template  v-slot:activator="{ on }">
                                              <v-switch 
                                                v-model="props.item.enabled" 
                                                hide-details 
                                                v-on="on" 
                                                class="switch-center" 
                                                @change="toggleRule(props.item,'org','enable')"  :style="$store.state.textFormatting.main">
                                              </v-switch>
                                            </template>
                                            <span :style="$store.state.textFormatting.main">Enable/Disable Rule</span>
                                          </v-tooltip>                                        
                                          <v-tooltip  v-if="user_type=='Analyst'" top eager close-delay=100>
                                            <template  v-slot:activator="{ on }">
                                              <v-switch 
                                                disabled
                                                v-model="props.item.enabled" 
                                                hide-details 
                                                v-on="on" 
                                                class="switch-center" 
                                                @change="toggleRule(props.item,'org','enable')"  :style="$store.state.textFormatting.main">
                                              </v-switch>
                                            </template>
                                            <span :style="$store.state.textFormatting.main">Enable/Disable Rule</span>
                                          </v-tooltip>                                        
                                    </td>
                                </tr>
                                
                            </template>
                        <template slot="no-data">
                            <v-alert :value="true" color="success" icon="warning" :style="$store.state.textFormatting.main">
                            No Organisation Rules, click 'ADD RULE +' to add one
                            </v-alert>
                        </template>
                    </v-data-table>


                </v-card>
            </v-flex>
            <v-flex xs12 md10 class="py-2 px-2">
                <v-toolbar dark flat color="secondary">
                    <v-toolbar-title @click="showUserASNs=!showUserASNs" :style="$store.state.textFormatting.headers"> {{username}}'s Rules </v-toolbar-title>
                    <v-progress-circular v-if="loading2" indeterminate color="white"></v-progress-circular>
                    <v-spacer></v-spacer>
                    <v-flex shrink align-center justify-center>
                        <br />
                        <v-switch v-model="showAdvanced" @change="advancedChange()">
                            <template v-slot:label>
                                <div :style="$store.state.textFormatting.main"><b>Advanced settings</b></div>
                            </template>
                        </v-switch>
                    </v-flex>
                    <downloadcsv
                        class   = "btn btn-default"
                        :data   = "user_download_rules"
                        name = "userRules.csv">
                        <v-tooltip top eager color="primary" id="exit-app">
                            <template v-slot:activator="{ on }">
                                <v-btn small dark v-on="on" color="success" :style="$store.state.textFormatting.main"> <v-icon>save_alt</v-icon> Export User Rules </v-btn>
                            </template>
                            <span :style="$store.state.textFormatting.main">CSV of user Rules</span>
                        </v-tooltip>        
                    </downloadcsv>
                    <v-btn @click="clickRule(null,'user','add')" color="success" :style="$store.state.textFormatting.main"> Add Rule <v-icon>add</v-icon> </v-btn>
                    
                    <info>
                        <template slot>
                            Add an Alert Rule to your specific user, or remove/ edit existing rule.
                        </template>
                         <template slot="body">
                            The alerting mechanism works in the following way: <br />
                            
                            <p> <h2> STANDARD ALERTING (no advanced settings) </h2> </p>
                            <ul>
                                <li> A user can define a Rule based on an ASN or Prefix, give it a name (required), and will be alerted upon <b>ownership assertions</b>, <b>Path Changes</b> and/or <b>First Seen Prefixes</b> where the Prefix(es) and ASN(s) are matched in observed BGP messages. </li>
                                <li> Users can specify <b>which event types to be alerted upon</b>, of which there are 3 in total: 
                                    <ul>
                                        <li><b>Ownership Assertions</b></li>
                                        <li><b>Path Changes</b></li>
                                        <li><b>First Seens</b></li>
                                    </ul>
                                    There are descriptions of each of these in <b>hover over panels throughout the page - hover your cursor where you see any of these terms</b>
                                </li>
                            </ul>

                            <ul>
                                <li> The user can also decide whether to receive emails from these alerts, again split out by the event type </li>
                                <li> Hijack and Impact score thresholds can also be set, which can further refine down the amount of alerts you will receive. </li>
                                <li>
                                    The scores of the <b> event </b> are compared with the <b> thresholds </b>. If hijack <b> and/or (set by the user for each ASN - default AND) </b>
                                    impact scores are <b> greater than or equal to </b> the given thresholds, a notification/ alert is created in the mySQL table. There are some predefined thresholds:
                                    <ul>
                                        <li> <b> Regular: H = 30, I = 10, V = 5 </b> for when you are interested in some potentially dangerous anomalies, but not the less dangerous ones </li>
                                        <li> <b> High Importance: H = 20, I = 8, V = 2 </b> for when you want to know about most anomalies, but not those we deem very unlikely to be hijack events </li>
                                        <li> <b> Low Importance: H = 50, I = 25, V = 10 </b> for when you only need to see the very high risk anomalies </li>
                                        <li> <b> Show All: H = 0, I = 0, V = 0 (default)</b> will trigger alerts for all incoming events </li>
                                    </ul>
                                </li>
                                
                                <li> When an alert occurs with emails turned on, the following occurs:
                                    <ul>
                                        <li> An initial email is sent out, alerting the user of the alert beginning </li>
                                        <li> When additional events occur with the same basic data (in the same 5 min window), and the total 
                                            passes the <b>Repeat Email Alert Threshold - set by the user</b>, an additional email is sent out </li>
                                        <li> This continues until the 5 minute window elapses, at which point a new notification / alert will 
                                            begin </li>
                                    </ul>
                                </li>
                                <li> <b>Lastly</b>, the user may add multiple ASNs to an <b>AS Group</b>. ASNs in a common group behave in one of the following, user defined, ways:
                                    <ul>
                                        <li><b>EXCLUDE:</b> Ownership assertions between ASNs in the group will <b>not create alerts</b> e.g. if I have AS 1 and AS 2 in a group and AS 2 asserts ownership over one of AS 1's prefixes, I will not be alerted </li>
                                        <li><b>HIJACK:</b> Ownership assertions between ASNs in the group will have their hijack scores overriden and set to the user defined score </li>
                                    </ul>
                                </li>
                            </ul>

                            <p> <h2> ADVANCED ALERTING (advanced settings) </h2> </p>
                            <ul>
                                <li> A user can define score overrides for a given rule to raise the immportance of an alert. These scores are visible only to the Organisation or User.</li>
                            </ul>
                            <ul>
                                <li> A user can also define more detailed Prefix, Origin and Path based rules as follows:: 
                                    <ul>
                                        <li><b>Additional "Except" claused for Prefix, Origin and Path</b> - these allow the rules to be fine tuned to exclude alerts based on specific criteria. e.g. alert on ownership assertions for prefix 1.1.1.0/24 except where ASN is 1234</li>
                                        <li><b>Optional Origin Alerting Conditions</b> - where an origin is normally triggered as a "new" or "old" origin, rules can be tuned to be specific about the direction of change. e.g. alert on ownership assertions for prefix 1.1.1.90/24 only when ASN 1234 is the new origin</li>
                                        <li><b>Path Position</b> - this allows users to trigger alerts where an ASN is found "anywhere" in the path or at the "origin + 1" postion</li>
                                    </ul>
                                </li>
                            </ul>
                            <br />
                            <b> NOTE: Updates to thresholds do not work retrospectively, so will not alter previously created alerts and alert counts. There is also a delay in the analytics engine picking up the changes, so you may continue to receive alerts after making changes. </b>
                        </template>
                    </info>
                </v-toolbar>  
                <v-card v-if="showUserASNs">  

                    <v-data-table :headers="alertHeaders" :items="userRules" :key="id"
                            :rows-per-page-items="rowsPerPageItems" :pagination.sync="pagination" > 
                            <template slot="headerCell" slot-scope="props">
                                <v-tooltip top eager>
                                <span slot="activator" :style="$store.state.textFormatting.main">
                                    {{ props.header.text }}
                                </span>
                                <span :style="$store.state.textFormatting.main">
                                    {{ props.header.ad_text }}
                                </span>
                                </v-tooltip>
                            </template>   
                            <template slot="items" slot-scope="props">
                                <tr>
                                    <td class="text-xs-center" :style="$store.state.textFormatting.main"> {{ props.item.alertgroup }} </td>
                                    <td class="text-xs-center" :style="$store.state.textFormatting.main"> {{ props.item.alertname }} </td>
                                    <td class="text-xs-center" :style="$store.state.textFormatting.main"> <div style="text-align:right">
                                        Assertions : <v-icon v-if="props.item.alerts.assertion"> check </v-icon><v-icon v-if="!props.item.alerts.assertion"> clear </v-icon>(<v-icon v-if="props.item.emails.assertion"> check </v-icon><v-icon v-if="!props.item.emails.assertion"> clear </v-icon>)<br >
                                        Path Changes :  <v-icon v-if="props.item.alerts.pathchange"> check </v-icon><v-icon v-if="!props.item.alerts.pathchange"> clear </v-icon>(<v-icon v-if="props.item.emails.pathchange"> check </v-icon><v-icon v-if="!props.item.emails.pathchange"> clear </v-icon>)<br >
                                        First Seen :  <v-icon v-if="props.item.alerts.firstseen"> check </v-icon><v-icon v-if="!props.item.alerts.firstseen"> clear </v-icon>(<v-icon v-if="props.item.emails.firstseen"> check </v-icon><v-icon v-if="!props.item.emails.firstseen"> clear </v-icon>)<br ></div>
                                          </td>
                                    <td class="text-xs-center" :style="$store.state.textFormatting.main">
                                        <span v-for="origin in props.item.filter.origin.is">{{ origin }}<br /></span></td>
                                    <td class="text-xs-center" :style="$store.state.textFormatting.main">
                                        <span v-for="prefix in props.item.filter.prefix.is">{{ prefix }}<br /></span></td>
                                    <td class="text-xs-center" :style="$store.state.textFormatting.main">
                                        <span v-for="path in props.item.filter.path.contains">{{ path }}<br /></span></td>

                                    <td class="text-xs-center" :style="$store.state.textFormatting.main" v-if="showAdvanced"> {{ props.item.filter.scores.hijack }}</td>
                                    
                                    <td class="text-xs-center" :style="$store.state.textFormatting.main" v-if="showAdvanced"> {{ props.item.filter.scores.andor }}</td>
                                    <td class="text-xs-center" :style="$store.state.textFormatting.main" v-if="showAdvanced"> {{ props.item.filter.scores.impact }}</td>
                                    <td class="text-xs-center" :style="$store.state.textFormatting.main" v-if="showAdvanced"> {{ props.item.emails.repeat }}</td>

                                    <td class="text-xs-center" :style="$store.state.textFormatting.main"> {{ props.item.notification_count }} </td>
                                    <td class="text-xs-center">
                                        <v-tooltip top eager close-delay=100>
                                            <template  v-slot:activator="{ on }">
                                                <v-icon v-on="on" small class="mr-2" @click="clickRule(props.item,'user','edit')" :style="$store.state.textFormatting.main"> edit </v-icon>
                                            </template>
                                            <span :style="$store.state.textFormatting.main">Edit Rule</span>
                                        </v-tooltip>
                                        
                                        <v-tooltip top eager close-delay=100>
                                            <template  v-slot:activator="{ on }">
                                                <v-icon v-on="on" small class="mr-2" @click="prepRemoveRule(props.item,'user')" :style="$store.state.textFormatting.main"> delete </v-icon>
                                            </template>
                                            <span :style="$store.state.textFormatting.main">Remove Rule</span>
                                        </v-tooltip>                                        
                                    </td>
                                    <td class="text-xs-center">
                                          <v-tooltip top eager close-delay=100>
                                            <template  v-slot:activator="{ on }">
                                              <v-switch 
                                                v-model="props.item.enabled" 
                                                hide-details 
                                                v-on="on" 
                                                class="switch-center" 
                                                @change="toggleRule(props.item,'user','enable')"  :style="$store.state.textFormatting.main">
                                              </v-switch>
                                            </template>
                                            <span :style="$store.state.textFormatting.main">Enable/Disable Rule</span>
                                          </v-tooltip>                                        
                                    </td>
                                </tr>
                                
                            </template>
                        <template slot="no-data">
                            <v-alert :value="true" color="success" icon="warning" :style="$store.state.textFormatting.main">
                            No User Rules, click 'ADD RULE +' to add one
                            </v-alert>
                        </template>
                    </v-data-table>


                    <v-layout align-right justify-center v-if="userASNs.length > 1">
                        <v-spacer />
                        <v-flex shrink class="py-2 px-2">
                            <v-btn @click="showPurge=true" color="red">Delete All ASNs</v-btn>
                        </v-flex>
                    </v-layout>
                </v-card>
            </v-flex>
            <v-flex xs12 xl10 v-if="showEdit" class="px-2 py-2">
                <v-toolbar flat dark color="secondary">
                    <v-toolbar-title v-if="edType == 'Add'" :style="$store.state.textFormatting.headers">Add Rule to your {{ addType }} </v-toolbar-title>
                    <v-toolbar-title v-if="edType == 'Edit'" :style="$store.state.textFormatting.headers">Edit/View {{ addType }} Rule "{{ ruleForm.alertname }}" </v-toolbar-title>
                    <v-spacer></v-spacer>
                    <v-flex shrink align-center justify-center>
                        <br />
                        <v-switch v-model="showAdvanced" @change="advancedChange()">
                            <template v-slot:label>
                                <div :style="$store.state.textFormatting.main"><b>Advanced settings</b></div>
                            </template>
                        </v-switch>
                    </v-flex>
                    <div v-if="user_type!='Analyst' || addType=='user'">
                      <v-btn color="#3F8993" class="mr-4" @click="csvOpen" v-if="edType == 'Add'" :style="$store.state.textFormatting.main"> Import from CSV </v-btn>
                      <v-btn color="success" class="mr-4" @click="addRule" v-if="edType == 'Add'" :disabled="!valid" :style="$store.state.textFormatting.main"> Submit </v-btn>
                      <v-btn color="success" class="mr-4" @click="editRule" v-if="edType == 'Edit'" :disabled="!valid" :style="$store.state.textFormatting.main"> Submit </v-btn>
                      <!-- <v-btn color="red" class="mr-4" @click="prepRemoveASN" v-if="edType == 'Edit'"> Remove ASN </v-btn> !-->
                    </div>
                    <div v-if="user_type=='Analyst' && addType!='user'">
                      <v-btn color="#3F8993" class="mr-4" :disabled="true"  :style="$store.state.textFormatting.main"> Submit </v-btn>
                    </div>
                    <v-btn class="mx-2" fab small color="blue" @click="shrinkEditWindow">
                          <h3 :style="$store.state.textFormatting.main">-</h3>
                    </v-btn>
                </v-toolbar>
                <v-card>
                    <v-container fluid>
                        <v-card-text>
                            <v-form ref="ruleForm" v-model="valid" lazy-validation>
                                <v-layout wrap justify-center row>
                                    <v-flex xs6 class="px-2">
                                        <v-text-field 
                                            :style="$store.state.textFormatting.main" 
                                            v-model="ruleForm.alertname" 
                                            label="Rule Name"></v-text-field>
                                    </v-flex>
                                    <v-flex xs6 class="px-2">
                                      <v-combobox
                                        :style="$store.state.textFormatting.main"
                                        v-model="ruleForm.alertgroup"
                                        :items="ruleForm.Groups"
                                        hint="Select group"
                                        persistent-hint
                                        >
                                          <template v-slot:no-data>
                                            <v-list-tile>
                                              <v-list-tile-title>
                                                No results matching. Press <kbd>enter</kbd> to create a new one
                                              </v-list-tile-title>
                                            </v-list-tile>
                                          </template>
                                        </v-combobox>
                                    </v-flex>
                                </v-layout>
                                <v-container fluid grid-list-md>
                                    <v-layout row wrap>
<!-- Alert Selections Section -->
                                        <v-flex md6 xs6>
                                            <v-toolbar dark flat color="secondary">
                                                <v-toolbar-title>Alert Type Selections</v-toolbar-title>
                                                <v-spacer></v-spacer>
                                                <info>
                                                    <template slot>
                                                        Select the types of alerts you would like to trigger.
                                                    </template>
                                                    <template slot="body">
                                                        <p>Here, you can select the main type of pre-categorised events you would like to receive alerts for: <br /></p>                                                        
                                                        <ul>
                                                            <li><b>Ownership Assertions</b></li>
                                                            <li><b>Path Changes</b></li>
                                                            <li><b>First Seens</b></li>
                                                        </ul>
                                                        <p>For each event type, you can select to recieve just alerts in the tool or additionally receive an email as well</p>
                                                        <p>Where email alerts have been enabled, you can also set the threshold of how many alerts are required to trigger follow up emails</p>
                                                        <p>NOTE: To receive email alerts, you must enable emails in the <v-btn small @click="routeNewWindow('User Admin')" color="success" :style="$store.state.textFormatting.main">USER SETTINGS</v-btn> section</p>
                                                    </template>
                                                </info>
                                            </v-toolbar>
<!-- top of card -->
                                            <v-card>
                                                <v-layout justify-start row wrap>
<!-- ownership assertions -->
                                                    <v-flex>
                                                        <v-layout align-center row wrap style="margin-bottom: -20px; margin-top: -20px">
                                                            <v-flex class="px-3" d-flex xs4 >
                                                                <v-tooltip top eager close-delay=500 >
                                                                    <template  v-slot:activator="{ on }">
                                                                        <div slot="activator" v-on="on" :style="$store.state.textFormatting.main">Ownership Assertions</div>
                                                                    </template>
                                                                    <span :style="$store.state.textFormatting.main">Alerts where the origin (ownership) ASN has changed.</span>
                                                                </v-tooltip>
                                                            </v-flex>
                                                            <v-tooltip top eager close-delay=500>
                                                                <template v-slot:activator="{ on }">
                                                                    <v-flex class="px-3" d-flex xs2 v-on="on">
                                                                        <v-switch v-model="ruleForm.assertions">
                                                                            <template v-slot:label>
                                                                                <div :style="$store.state.textFormatting.main">Alerts</div>
                                                                            </template> 
                                                                        </v-switch>
                                                                    </v-flex>
                                                                </template>
                                                                <span :style="$store.state.textFormatting.main">Enable alerts for <b>Ownership Assertions</b>.</span>
                                                            </v-tooltip>
                                                            <v-tooltip top eager close-delay=500>
                                                                <template  v-slot:activator="{ on }">
                                                                    <v-flex class="px-3" d-flex xs3 v-on="on">
                                                                        <v-switch v-model="ruleForm.intraorg" :disabled="!ruleForm.assertions">
                                                                            <template v-slot:label>
                                                                                <div :style="$store.state.textFormatting.main">Intra Org</div>
                                                                            </template> 
                                                                        </v-switch>
                                                                    </v-flex>
                                                                </template>
                                                                <span v-if="!ruleForm.assertions" :style="$store.state.textFormatting.main">You must enable the alert before you enabling or disabling this feature.</span>
                                                                <span v-if="ruleForm.assertions" :style="$store.state.textFormatting.main">Enable to <b>include</b> ownership assertions between ASNs that belong to the same organisation.<br />By default these ownership assertions are filtered out.</span>
                                                            </v-tooltip>
                                                            <v-tooltip top eager close-delay=500>
                                                                <template  v-slot:activator="{ on }">
                                                                    <v-flex class="px-3" xs3 v-on="on" d-vlex>
                                                                        <v-switch v-model="ruleForm.assertions_email" :disabled="!ruleForm.assertions">
                                                                            <template v-slot:label>
                                                                                <div :style="$store.state.textFormatting.main">Emails</div>
                                                                            </template> 
                                                                        </v-switch>
                                                                    </v-flex>
                                                                </template>
                                                                <span v-if="!ruleForm.assertions" :style="$store.state.textFormatting.main">You must enable the alert before you can enable emails</span>
                                                                <span v-if="ruleForm.assertions" :style="$store.state.textFormatting.main">Enable emails for <b>Ownership Assertions</b>. </span>
                                                            </v-tooltip>
                                                            <v-spacer></v-spacer>
                                                        </v-layout>
                                                    </v-flex>
<!-- Path Changes                                                 -->
                                                    <v-flex>
                                                        <v-layout align-center row wrap style="margin-bottom: -20px; margin-top: -20px">
                                                            <v-flex class="px-3" d-flex xs4>
                                                                <v-tooltip top eager close-delay=500 >
                                                                    <template  v-slot:activator="{ on }">
                                                                        <div style="width:200px" slot="activator" v-on="on" :style="$store.state.textFormatting.main">Path Changes</div>
                                                                    </template>
                                                                    <span :style="$store.state.textFormatting.main">Alerts where the origin (ownership) ASN has <b>NOT</b> changed.</span>
                                                                </v-tooltip>
                                                            </v-flex>
                                                            <v-tooltip top eager close-delay=500 >
                                                                <template  v-slot:activator="{ on }">
                                                                    <v-flex class="px-3" d-flex xs5 v-on="on">
                                                                        <v-switch v-model="ruleForm.pathchanges">
                                                                            <template v-slot:label>
                                                                                <div :style="$store.state.textFormatting.main">Alerts</div>
                                                                            </template> 
                                                                        </v-switch>
                                                                    </v-flex>
                                                                </template>
                                                                <span :style="$store.state.textFormatting.main">Enable alerts for <b>Path Changes</b>.</span>
                                                            </v-tooltip>
                                                            <v-tooltip top eager close-delay=500>
                                                                <template v-slot:activator="{ on }">
                                                                    <v-flex class="px-3" xs3 v-on="on" d-flex>
                                                                        <v-switch v-model="ruleForm.pathchanges_email" :disabled="!ruleForm.pathchanges">
                                                                            <template v-slot:label>
                                                                                <div :style="$store.state.textFormatting.main">Emails</div>
                                                                            </template> 
                                                                        </v-switch>
                                                                    </v-flex>
                                                                </template>
                                                                <span v-if="!ruleForm.pathchanges" :style="$store.state.textFormatting.main">You must enable the alert before you can enable emails</span>
                                                                <span v-if="ruleForm.pathchanges" :style="$store.state.textFormatting.main">Enable emails for <b>Path Changes</b>. </span>
                                                            </v-tooltip>
                                                            <v-spacer></v-spacer>
                                                        </v-layout>
                                                    </v-flex>
<!-- First Seens -->                                   
                                                    <v-flex>
                                                        <v-layout align-center row wrap style="margin-bottom: -20px; margin-top: -20px">
                                                            <v-flex class="px-3" d-flex xs4>
                                                                <v-tooltip top eager close-delay=500 >
                                                                    <template  v-slot:activator="{ on }">
                                                                        <div style="width:200px" slot="activator" v-on="on" :style="$store.state.textFormatting.main">First Seen</div>
                                                                    </template>
                                                                    <span :style="$store.state.textFormatting.main">Alerts where the Prefix and Origin have not been seen before.</span>
                                                                </v-tooltip>
                                                            </v-flex>
                                                            <v-tooltip top eager close-delay=500>
                                                                <template v-slot:activator="{ on }">
                                                                    <v-flex class="px-3" d-flex xs5 v-on="on">
                                                                        <v-switch v-model="ruleForm.firstseens">
                                                                            <template v-slot:label>
                                                                                <div :style="$store.state.textFormatting.main">Alerts</div>
                                                                            </template> 
                                                                        </v-switch>
                                                                    </v-flex>
                                                                </template>
                                                                <span :style="$store.state.textFormatting.main">Enable alerts for <b>First Seens</b>.</span>
                                                            </v-tooltip>
                                                            <v-tooltip top eager close-delay=500>
                                                                <template v-slot:activator="{ on }">
                                                                    <v-flex class="px-3" xs3 v-on="on" d-flex>
                                                                        <v-switch v-model="ruleForm.firstseens_email" :disabled="!ruleForm.firstseens">
                                                                            <template v-slot:label>
                                                                                <div :style="$store.state.textFormatting.main">Emails</div>
                                                                            </template> 
                                                                        </v-switch>
                                                                    </v-flex>
                                                                </template>
                                                                <span v-if="!ruleForm.pathchanges" :style="$store.state.textFormatting.main">You must enable the alert before you can enable emails</span>
                                                                <span v-if="ruleForm.pathchanges" :style="$store.state.textFormatting.main">Enable emails for <b>First Seens</b>. </span>
                                                            </v-tooltip>
                                                            <v-spacer></v-spacer>
                                                        </v-layout>
                                                    </v-flex>

                                                    <v-flex>
                                                        <v-layout align-center row wrap style="margin-bottom: -20px; margin-top: -20px">
                                                            <v-flex xs4 class="px-3" d-flex>
                                                                <v-tooltip top eager close-delay=500 >
                                                                    <template  v-slot:activator="{ on }">
                                                                        <div v-on="on">
                                                                            <div slot="activator" v-on="on" :style="$store.state.textFormatting.main">Email Repeat</div>
                                                                        </div>
                                                                    </template>
                                                                    <span :style="$store.state.textFormatting.main">
                                                                        How many <b>events</b> it takes for an <b>additional email</b> to be sent out re-alerting you to the ongoing episode. <br />
                                                                        e.g. if I set V = 10, I will be sent an initial notifying email, <br /> then once the alert has at least 10 instances of events in it, I will receive an additional email.
                                                                    </span>
                                                                </v-tooltip>
                                                            </v-flex>
                                                            <v-flex xs5 class="px-3" d-flex>
                                                                <v-slider :disabled="!ruleForm.firstseens_email&&!ruleForm.pathchanges_email&&!ruleForm.assertions_email" v-model="ruleForm.repeat" min="0" max="99"></v-slider>
                                                            </v-flex>
                                                            <v-flex xs2 class="px-3" d-flex>
                                                                <v-text-field :disabled="!ruleForm.firstseens_email&&!ruleForm.pathchanges_email&&!ruleForm.assertions_email" type="number" v-model="ruleForm.repeat" min="0" max="99"></v-text-field>
                                                            </v-flex>
                                                        </v-layout>
                                                    </v-flex>

                                                </v-layout>


                                            </v-card>
<!-- end of card -->
                                        </v-flex>
<!-- end of section                                         -->


<!-- start of sliders section -->
                                        <v-flex xs6>
                                            <v-toolbar dark flat color="secondary">
                                                <v-toolbar-title>Alert Score Thresholds</v-toolbar-title>
                                                <v-spacer></v-spacer>
                                                <info>
                                                    <template slot>
                                                        Set the score thresholds for the alerts you have chosen.
                                                    </template>
                                                    <template slot="body">
                                                        <p>Here, you can select thresholds for the Hijack and Impact scores.<br /></p>
                                                        <p>The AND/OR option determines whether the scores should BOTH be above their respective thresholds or whether just one of them needs to be</p>
                                                        <p>There is also the option to set the thresholds to pre-set values from the drop-down options.</p>
                                                    </template>
                                                </info>
                                            </v-toolbar>
<!-- top of card -->
                                            <v-card>
                                                <v-layout align-center row wrap>

                                                    <v-flex>
                                                        <v-layout align-center row wrap style="margin-bottom: -20px; margin-top: -20px">
                                                            <v-flex xs4 class="px-3" d-flex>
                                                                <v-tooltip top eager close-delay=500 >
                                                                    <template  v-slot:activator="{ on }">
                                                                        <div v-on="on">
                                                                            <div slot="activator" v-on="on" :style="$store.state.textFormatting.main">Hijack Score</div>
                                                                        </div>
                                                                    </template>
                                                                    <span :style="$store.state.textFormatting.main">
                                                                        <b> The higher you set the threshold here, the less alerts you will receive (i.e. less noise): </b> <br />
                                                                        H (0 - 20): Low chance of being hijacking event. <br />
                                                                        H (20 - 50): Reasonable chance of hijacking event. <br />
                                                                        H (50 +): High chance of hijacking event/ misconfigured router.
                                                                    </span>
                                                                </v-tooltip>
                                                            </v-flex>
                                                            <v-flex xs5 class="px-3" d-flex>
                                                                <v-slider v-model="ruleForm.hijackscore" min="0" max="99"></v-slider>
                                                            </v-flex>
                                                            <v-flex xs2 class="px-3" d-flex>
                                                                <v-text-field type="number" v-model="ruleForm.hijackscore" min="0" max="99"></v-text-field>
                                                            </v-flex>
                                                        </v-layout>
                                                    </v-flex>

                                                    <v-flex>
                                                        <v-layout align-center row wrap style="margin-bottom: -20px; margin-top: -20px">
                                                            <v-flex xs4 class="px-3">
                                                                <v-tooltip top eager close-delay=500 >
                                                                    <template  v-slot:activator="{ on }">
                                                                        <div v-on="on">
                                                                            <div slot="activator" v-on="on" :style="$store.state.textFormatting.main">Scoring AND/OR</div>
                                                                        </div>
                                                                    </template>
                                                                    <span :style="$store.state.textFormatting.main">
                                                                        Let's say we have hijack and impact settings as follows: <b> H = 10, I = 5 </b>
                                                                        <br /> An event comes in with <b> H = 15 and I = 2 </b>.
                                                                        <br /> <b> And: </b> BOTH hijack and impact scores must exceed thresholds, so in this case I am <b>not alerted</b> (impact not satisfied).
                                                                        <br /> <b> Or: </b> EITHER hijack or impact score must exceed thresholds, so I <b>am alerted</b>.
                                                                    </span>
                                                                </v-tooltip>      
                                                            </v-flex>
                                                            <v-flex xs4 class="px-3" d-flex>
                                                                <v-select :style="$store.state.textFormatting.main" slot="activator" v-model="ruleForm.and_or" :items="andOr" item-text="name" item-value="value" > </v-select>
                                                            </v-flex>
                                                        </v-layout>
                                                    </v-flex>

                                                    <v-flex>
                                                        <v-layout align-center row wrap style="margin-bottom: -20px; margin-top: -20px">
                                                            <v-flex xs4 class="px-3" d-flex>
                                                                <v-tooltip top eager close-delay=500 >
                                                                    <template  v-slot:activator="{ on }">
                                                                        <div v-on="on">
                                                                            <div slot="activator" v-on="on" :style="$store.state.textFormatting.main">Impact Score</div>
                                                                        </div>
                                                                    </template>
                                                                    <span :style="$store.state.textFormatting.main">
                                                                        <b> The higher you set the threshold here, the less alerts you will receive (i.e. less noise): </b> <br />
                                                                        I (0 - 8): Low impact (based on ASN old/new geo comparison, subownership assertion scope i.e. how specific advertisment is compared to parent prefix). <br />
                                                                        I (8 - 20): Medium impact. <br />
                                                                        I (20 - 50): High impact. <br />
                                                                        I (50 +): Reserved for <b>restricted prefixes</b> in your organisation.
                                                                    </span>
                                                                </v-tooltip>
                                                            </v-flex>
                                                            <v-flex xs5 class="px-3" d-flex>
                                                                <v-slider v-model="ruleForm.impactscore" min="0" max="99"></v-slider>
                                                            </v-flex>
                                                            <v-flex xs2 class="px-3" d-flex>
                                                                <v-text-field type="number" v-model="ruleForm.impactscore" min="0" max="99"></v-text-field>
                                                            </v-flex>
                                                        </v-layout>
                                                    </v-flex>

                                                    <v-flex>
                                                        <v-layout align-center row wrap style="margin-bottom: -20px; margin-top: -20px">
                                                            <v-flex xs4 class="px-3" d-flex>
                                                                <v-tooltip top eager close-delay=500 >
                                                                    <template  v-slot:activator="{ on }">
                                                                        <div v-on="on">
                                                                            <div slot="activator" v-on="on" :style="$store.state.textFormatting.main">Preset Thresholds</div>
                                                                        </div>
                                                                    </template>
                                                                    <span :style="$store.state.textFormatting.main">
                                                                        <b> The higher you set the threshold here, the less alerts you will receive (i.e. less noise): </b> <br />
                                                                        I (0 - 8): Low impact (based on ASN old/new geo comparison, subownership assertion scope i.e. how specific advertisment is compared to parent prefix). <br />
                                                                        I (8 - 20): Medium impact. <br />
                                                                        I (20 - 50): High impact. <br />
                                                                        I (50 +): Reserved for <b>restricted prefixes</b> in your organisation.
                                                                    </span>
                                                                </v-tooltip>
                                                            </v-flex>
                                                            <v-flex xs4 class="px-3" d-flex>
                                                                <v-select :style="$store.state.textFormatting.main" v-model="form.type" :items="asnTypes" item-text="name" item-value="value" v-on:change="updateDefs"> </v-select>
                                                            </v-flex>
                                                        </v-layout>
                                                    </v-flex>
                                                </v-layout>
                                                
                                            </v-card>
                                    </v-flex>            
                                </v-layout>


                                <v-layout v-if="showAdvanced" row wrap>
<!-- start of overrides section -->
                                        <v-flex xs12>
                                            <v-toolbar dark flat color="secondary">
                                                <v-toolbar-title>Alert Score Overrides</v-toolbar-title>
                                                <v-spacer></v-spacer>
                                                <info>
                                                    <template slot>
                                                        Hijack and Impact Score Overrides.
                                                    </template>
                                                    <template slot="body">
                                                        <p>Here, you can set Hijack and Impact score overrides that apply to this alert rule.<br /></p>
                                                        <p>This can be used to highlight or raise the importance of an event for you or your organisation based on the prefix and/or ASN you are monitoring.</p>
                                                    </template>
                                                </info>
                                            </v-toolbar>
<!-- top of card -->
                                            <v-card>
<!-- hijack override -->
                                                <v-layout align-center row wrap>
                                                    <v-tooltip top eager close-delay=500>
                                                        <template v-slot:activator="{ on }">
                                                            <v-flex class="px-3" d-flex xs3 v-on="on">
                                                                <v-switch v-model="ruleForm.overrides_hijack">
                                                                    <template v-slot:label>
                                                                        <div :style="$store.state.textFormatting.main">Enable Hijack Score Override</div>
                                                                    </template> 
                                                                </v-switch>
                                                            </v-flex>
                                                        </template>
                                                        <span :style="$store.state.textFormatting.main">Enable hijack score override for this alert rule.</span>
                                                    </v-tooltip>

                                                    <v-flex>
                                                        <v-layout align-center row wrap style="margin-bottom: -20px; margin-top: -20px">
                                                            <v-flex xs3 class="px-3" d-flex>
                                                                <v-tooltip top eager close-delay=500 >
                                                                    <template  v-slot:activator="{ on }">
                                                                        <div v-on="on">
                                                                            <div slot="activator" v-on="on" :style="$store.state.textFormatting.main">Hijack Score Override Value</div>
                                                                        </div>
                                                                    </template>
                                                                    <span :style="$store.state.textFormatting.main">
                                                                        <b> The higher you set the threshold here, the less alerts you will receive (i.e. less noise): </b> <br />
                                                                        H (0 - 20): Low chance of being hijacking event. <br />
                                                                        H (20 - 50): Reasonable chance of hijacking event. <br />
                                                                        H (50 +): High chance of hijacking event/ misconfigured router.
                                                                    </span>
                                                                </v-tooltip>
                                                            </v-flex>
                                                            <v-flex xs6 class="px-3" d-flex>
                                                                <v-slider :disabled="!ruleForm.overrides_hijack" v-model="ruleForm.overrides_hijack_score" min="0" max="99"></v-slider>
                                                            </v-flex>
                                                            <v-flex xs2 class="px-3" d-flex>
                                                                <v-text-field type="number" :disabled="!ruleForm.overrides_hijack" v-model="ruleForm.overrides_hijack_score" min="0" max="99"></v-text-field>
                                                            </v-flex>
                                                        </v-layout>
                                                    </v-flex>
                                                </v-layout>
<!-- impact override -->
                                                <v-layout align-center row wrap>
                                                    <v-tooltip top eager close-delay=500>
                                                        <template v-slot:activator="{ on }">
                                                            <v-flex class="px-3" d-flex xs3 v-on="on">
                                                                <v-switch v-model="ruleForm.overrides_impact">
                                                                    <template v-slot:label>
                                                                        <div :style="$store.state.textFormatting.main">Enable Impact Score Override</div>
                                                                    </template> 
                                                                </v-switch>
                                                            </v-flex>
                                                        </template>
                                                        <span :style="$store.state.textFormatting.main">Enable impact score override for this alert rule.</span>
                                                    </v-tooltip>

                                                    <v-flex>
                                                        <v-layout align-center row wrap style="margin-bottom: -20px; margin-top: -20px">
                                                            <v-flex xs3 class="px-3" d-flex>
                                                                <v-tooltip top eager close-delay=500 >
                                                                    <template  v-slot:activator="{ on }">
                                                                        <div v-on="on">
                                                                            <div slot="activator" v-on="on" :style="$store.state.textFormatting.main">Impact Score Override Value</div>
                                                                        </div>
                                                                    </template>
                                                                    <span :style="$store.state.textFormatting.main">
                                                                        <b> The higher you set the threshold here, the less alerts you will receive (i.e. less noise): </b> <br />
                                                                        H (0 - 20): Low chance of being hijacking event. <br />
                                                                        H (20 - 50): Reasonable chance of hijacking event. <br />
                                                                        H (50 +): High chance of hijacking event/ misconfigured router.
                                                                    </span>
                                                                </v-tooltip>
                                                            </v-flex>
                                                            <v-flex xs6 class="px-3" d-flex>
                                                                <v-slider :disabled="!ruleForm.overrides_impact" v-model="ruleForm.overrides_impact_score" min="0" max="99"></v-slider>
                                                            </v-flex>
                                                            <v-flex xs2 class="px-3" d-flex>
                                                                <v-text-field type="number" :disabled="!ruleForm.overrides_impact"  v-model="ruleForm.overrides_impact_score" min="0" max="99"></v-text-field>
                                                            </v-flex>
                                                        </v-layout>
                                                    </v-flex>
                                                </v-layout>
                                            </v-card>
                                    </v-flex>            
                                </v-layout>




                                <v-layout row wrap>
                                    <v-flex md4 xs4>
                                        <v-toolbar dark flat color="secondary">
                                            <v-toolbar-title>Prefix Filters</v-toolbar-title>
                                              <!-- <v-btn color="#3F8993" class="mr-4" @click="showAssist=true" :style="$store.state.textFormatting.main"> Search </v-btn> -->
                                            <v-spacer></v-spacer>
                                              <info>
                                                  <template slot>
                                                      Filter alerts based on prefixes.
                                                  </template>
                                                  <template slot="body">
                                                      <p>Here, you can add a list of prefixes that you would like to include in you alerts.</p>
                                                      <p>Prefixes must be in CIDR format (e.g. 192.168.1.0/24)</p>
                                                      <p>By default, the rule will trigger for exact matches only but you can include matches for subnets of the list provided. (i.e. a sub-assertion)</p>
                                                      <p>You are currently limited to 10 prefixes per rule</p>
                                                      <p>The Advanced settings allow you to enter prefixes that must NOT match. <br />(Logically [observed prefix] is in [prefix list] and not in [exception list] ) e.g. 
                                                        <ul>
                                                          <li>alert on Ownership Assertions where the prefix 192.10.128.0/23 and its subnets, but NOT 192.168.1.0/24 and its subnets.</li>
                                                          <li>alert on Ownership Assertions where the Origin is 1234 and prefixes do NOT match any of my known prefixes.</li>
                                                        </ul>
                                                      </p>
                                                  </template>
                                              </info>
                                        </v-toolbar>

                                        <v-card>
                                            <v-flex class="px-3">
                                              <v-layout>
                                                <v-flex xs6 d-flex>
                                                  <v-tooltip top eager close-delay=500 >
                                                    <template v-slot:activator="{ on }">
                                                      <v-switch v-on="on" v-model="ruleForm.prefixes_enabled">
                                                        <template v-slot:label>
                                                          <span v-on="on" :style="$store.state.textFormatting.main">Prefix is one of:</span>
                                                        </template>
                                                      </v-switch>
                                                    </template>
                                                    <span :style="$store.state.textFormatting.main">Where the observed prefix is one of the list below</span>
                                                  </v-tooltip>
                                                </v-flex>
                                                <v-flex xs6 d-flex>
                                                  <v-tooltip top eager close-delay=500 >
                                                    <template v-slot:activator="{ on }">
                                                      <v-switch v-on="on" v-model="ruleForm.include_subnets">
                                                        <template v-slot:label>
                                                          <span v-on="on" :style="$store.state.textFormatting.main">including subnets</span>
                                                        </template>
                                                      </v-switch>
                                                    </template>
                                                    <span :style="$store.state.textFormatting.main">Where the observed prefix is a subnet of any of the list below</span>
                                                  </v-tooltip>                                                    
                                                </v-flex>
                                              </v-layout>
                                            </v-flex>

                                            <v-flex v-if="ruleForm.prefixes_enabled" class="px-3">
                                                <v-layout align-center justify-center>
                                                    <v-flex class="px-1">
                                                        <v-textarea :disabled="!ruleForm.prefixes_enabled" :style="$store.state.textFormatting.main" v-model="ruleForm.prefix_in"  label="Prefixes (one per line)"></v-textarea>
                                                    </v-flex>
                                                    <v-flex v-if="showAdvanced" class="px-1">
                                                        <v-textarea :disabled="!ruleForm.prefixes_enabled" :style="$store.state.textFormatting.main" v-model="ruleForm.prefix_notin"  label="Except (one per line)"></v-textarea>
                                                    </v-flex>
                                                </v-layout>
                                            </v-flex>
                                            <v-flex v-if="!ruleForm.prefixes_enabled" class="px-3">
                                                <v-layout align-center justify-center>
                                                    <v-flex md10 d-flex class="px-3">
                                                      <v-card style="text-align: center;" color="grey lighten-3" >
                                                          <div style="margin:5px;" :style="$store.state.textFormatting.main">Any (don't care)</div>
                                                      </v-card>
                                                    </v-flex>
                                                </v-layout>
                                            </v-flex>
                                        </v-card>
                                    </v-flex>

                                    <v-flex md4 xs4>
                                        <v-toolbar dark flat color="secondary">
                                            <v-toolbar-title>Origin Filters</v-toolbar-title>
                                            <v-spacer></v-spacer>
                                              <info>
                                                  <template slot>
                                                      Filter alerts based on origin ASNs.
                                                  </template>
                                                  <template slot="body">
                                                      <p>Here, you can add a list of ASNs that you would like to include in you alerts<br />when they appear at the origin in the path.</p>
                                                      <p>e.g. where a path is "49673 24811 3216 174 5400" and one of the ASNs in your list is "5400", this part of the rule will trigger.</p>
                                                      <p>ASNs must be numbers only (integers) with one entry per line</p>
                                                      <p>You are currently limited to 10 ASNs per rule</p>
                                                      <p>The Advanced settings allow you to enter Origins that must NOT match. <br />(Logically [observed origin] is in [origin list] and not in [exception list] ) e.g. 
                                                        <ul>
                                                          <li>alert on Ownership Assertions where the prefix is 192.10.1.0/24 and the origin does NOT match the known correct origin.</li>
                                                        </ul>
                                                      </p>
                                                  </template>
                                              </info>
                                        </v-toolbar>
                                        <v-card>
                                            <v-flex class="px-3">
                                                <v-layout>
                                                  <v-flex xs6 d-flex>
                                                    <v-tooltip top eager close-delay=500 >
                                                        <template  v-slot:activator="{ on }">
                                                            <v-switch v-on="on" v-model="ruleForm.origins_enabled">
                                                                <template v-slot:label>
                                                                    <span v-on="on" :style="$store.state.textFormatting.main">Origin is one of:</span>
                                                                </template>
                                                            </v-switch>
                                                        </template>
                                                        <span :style="$store.state.textFormatting.main">Where the observed origin is one of the list below</span>
                                                    </v-tooltip>
                                                  </v-flex>
                                                  <v-flex v-if="showAdvanced" xs6 d-flex>
                                                    <v-tooltip top eager close-delay=500 >
                                                        <template  v-slot:activator="{ on }">
                                                          <span v-on="on">
                                                            <v-select  :style="$store.state.textFormatting.main" slot="activator" v-model="ruleForm.origins_direction" :items="originDirection" item-text="name" item-value="value" > </v-select>
                                                          </span>
                                                        </template>
                                                        <span :style="$store.state.textFormatting.main">Choose to match the OLD Origin, the NEW Origin or EITHER Origin.</span>
                                                    </v-tooltip>
                                                  </v-flex>
                                                </v-layout>
                                            </v-flex>
                                            <v-flex v-if="ruleForm.origins_enabled" class="px-3">
                                                <v-layout align-center justify-center>
                                                    <v-flex class="px-1">
                                                        <v-textarea :style="$store.state.textFormatting.main" v-model="ruleForm.origins_in"  label="Origins (one per line)"></v-textarea>
                                                    </v-flex>
                                                    <v-flex v-if="showAdvanced" class="px-1">
                                                        <v-textarea :style="$store.state.textFormatting.main" v-model="ruleForm.origins_notin"  label="Except (one per line)"></v-textarea>
                                                    </v-flex>
                                                </v-layout>
                                            </v-flex>
                                            <v-flex v-if="!ruleForm.origins_enabled" class="px-3">
                                                <v-layout align-center justify-center>
                                                    <v-flex md10 d-flex class="px-3">
                                                      <v-card style="text-align: center;" color="grey lighten-3" >
                                                          <div style="margin:5px;" :style="$store.state.textFormatting.main">Any (don't care)</div>
                                                      </v-card>
                                                    </v-flex>
                                                </v-layout>
                                            </v-flex>
                                        </v-card>
                                    </v-flex>

                                    <v-flex md4 xs4>
                                        <v-toolbar dark flat color="secondary">
                                            <v-toolbar-title>Path Filters</v-toolbar-title>
                                            <v-spacer></v-spacer>
                                              <info>
                                                  <template slot>
                                                      Filter alerts based on an ASN in the path.
                                                  </template>
                                                  <template slot="body">
                                                      <p>Here, you can add a list of ASNs that you would like to include in your alerts<br />when they appear at in the path, but not as the origin.</p>
                                                      <p>e.g. where a path is "49673 24811 3216 174 5400" and one of the ASNs in your list is "3216", this part of the rule will trigger.</p>
                                                      <p>ASNs must be numbers only (integers) with one entry per line</p>
                                                      <p>You are currently limited to 10 ASNs per rule</p>
                                                      <p>The Advanced settings allow you to enter ASNs that must NOT match. <br />(Logically [observed path] contains ASNs in [ASN list] and not in [exception list] ) e.g. 
                                                        <ul>
                                                          <li>alert on Path Changes where the origin is 1234 but the path does NOT contain 2345.</li>
                                                          <li>alert on Path Changes where the origin is 1234 and the path contains 3456 but does NOT contain 2345.</li>
                                                        </ul>
                                                      </p>
                                                  </template>
                                              </info>
                                        </v-toolbar>
                                        <v-card>
                                            <v-flex class="px-3">
                                                <v-layout>
                                                  <v-flex xs6 d-flex>
                                                    <v-tooltip top eager close-delay=500 >
                                                        <template  v-slot:activator="{ on }">
                                                            <v-switch v-on="on" v-model="ruleForm.paths_enabled">
                                                                <template v-slot:label>
                                                                    <span v-on="on" :style="$store.state.textFormatting.main">Path contains any of:</span>
                                                                </template>
                                                            </v-switch>
                                                        </template>
                                                        <span :style="$store.state.textFormatting.main">Where the path contains any of the list below</span>
                                                    </v-tooltip>
                                                  </v-flex>
                                                  <v-flex v-if="showAdvanced" xs6 d-flex>
                                                    <v-tooltip top eager close-delay=500 >
                                                        <template  v-slot:activator="{ on }">
                                                          <span v-on="on">
                                                            <v-select :style="$store.state.textFormatting.main" slot="activator" v-model="ruleForm.path_position" :items="pathPositions" item-text="name" item-value="value" > </v-select>
                                                          </span>
                                                        </template>
                                                        <span :style="$store.state.textFormatting.main">Choose to match ANY ASN in the Path (except the origin) or the NEXT ASN from the origin.</span>
                                                    </v-tooltip>
                                                  </v-flex>
                                                </v-layout>
                                            </v-flex>
                                            <v-flex v-if="ruleForm.paths_enabled" class="px-3">
                                                <v-layout align-center justify-center>
                                                    <v-flex class="px-1">
                                                        <v-textarea :style="$store.state.textFormatting.main" v-model="ruleForm.path_contains"  label="ASNs (one per line)"></v-textarea>
                                                    </v-flex>
                                                    <v-flex v-if="showAdvanced" class="px-1">
                                                        <v-textarea :style="$store.state.textFormatting.main" v-model="ruleForm.path_notcontains"  label="Except (one per line)"></v-textarea>
                                                    </v-flex>
                                                </v-layout>
                                            </v-flex>
                                            <v-flex v-if="!ruleForm.paths_enabled" class="px-3">
                                                <v-layout align-center justify-center>
                                                    <v-flex md10 d-flex class="px-3">
                                                      <v-card style="text-align: center;" color="grey lighten-3" >
                                                          <div style="margin:5px;" :style="$store.state.textFormatting.main">Any (don't care)</div>
                                                      </v-card>
                                                    </v-flex>
                                                </v-layout>
                                            </v-flex>
                                        </v-card>
                                    </v-flex>
                                </v-layout>

                            </v-container>
                            <v-card>
                    </v-card>
                </v-form>

                        </v-card-text>
                    </v-container>
                </v-card>
        </v-flex>
        <v-flex xs12 md10 v-if="displayMessage">
            <v-card class="justify-right" :color="msgColor">
                <h3 :style="$store.state.textFormatting.main"> {{ message }} </h3>
            </v-card>
        </v-flex>
        </v-layout>
        <v-dialog v-model="showDeleteRuleCheck" width="500">
            <v-toolbar color="secondary" dark>
                <v-toolbar-title :style="$store.state.textFormatting.headers"> Rule: {{ deleteRule.alertname }} </v-toolbar-title> <v-spacer></v-spacer> <v-icon>edit</v-icon> 
            </v-toolbar>
            <v-card >
                <v-card-text>
                    <h3 :style="$store.state.textFormatting.main"> Are you sure you want to delete Rule: {{ deleteRule.alertname }}?</h3>
                    <br />
                    <h3 :style="$store.state.textFormatting.main" v-if="deleteRule"> 
                        Current settings:
                        <ul>
                            <li :style="$store.state.textFormatting.main" class="text-xs-left"> 
                                <b> Origins </b><br/>
                                  <span v-for="origin in deleteRule.filter.origin.is">{{ origin }}<br /></span>
                            </li>
                            <li :style="$store.state.textFormatting.main" class="text-xs-left"> 
                                <b> Prefixes </b><br/>
                                  <span v-for="prefix in deleteRule.filter.prefix.is">{{ prefix }}<br /></span>
                            </li>
                            <li :style="$store.state.textFormatting.main" class="text-xs-left"> 
                                <b> Path ASNs </b><br/>
                                  <span v-for="path in deleteRule.filter.path.contains">{{ path }}<br /></span>
                            </li>
                        </ul>
                    </h3>
                </v-card-text>
                <v-card-actions>
                    <v-btn class="green" dark @click="removeRule" :style="$store.state.textFormatting.main"> Yes, delete</v-btn>
                    <v-spacer></v-spacer>
                    <v-btn class="red" dark @click="showDeleteRuleCheck=false" :style="$store.state.textFormatting.main"> No, close </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>


        <v-dialog v-model="showCSV" :width="this.$store.state.textFormatting.dialog.b_width" persistent>
            <v-toolbar color="secondary" dark>
                <v-toolbar-title :style="$store.state.textFormatting.headers"> Import Rules from CSV ({{ addType }}) </v-toolbar-title>
                <v-spacer></v-spacer>
                <downloadcsv
                    class   = "btn btn-default"
                    :data   = "csvTemplate"
                    name = "csvTemplate.csv">
                    <v-tooltip top eager color="primary" id="exit-app">
                        <template v-slot:activator="{ on }">
                            <v-btn small dark v-on="on" color="success" :style="$store.state.textFormatting.main"> <v-icon>save_alt</v-icon> Template </v-btn>
                        </template>
                        <span :style="$store.state.textFormatting.main">CSV Template for ASN entry</span>
                    </v-tooltip>        
                </downloadcsv>
                <info>
                    <template>
                        Add multiple ASNs from CSV
                    </template>
                    <template slot="body">
                        <p> Download template CSV file </p>
                        <p> Enter your ASNs into said file, then save (as CSV) </p>
                        <p> Press the 'choose file' button to upload the CSV </p>
                        <p> Check the entries look correct in the table, then click submit </p>
                        <br />
                        <p> <b> NOTE: </b> Please enter the following settings as follows:
                            <ul>
                                <li> <b> alerting_type:</b> path / origin </li>
                                <li> <b> exclude_peer:</b> Y / N </li>
                                <li> <b> emails:</b> Y / N </li>
                                <li> <b> and_or:</b> and / or </li>
                                
                            </ul>
                        </p>
                        <p> <b> NOTE: </b> Non-integer ASNs, hijack, impact and validation scores will be set to 0</p>
                    </template>
                </info>
            </v-toolbar>
            <v-card justify-center align-center>
            
                <br />
                <v-flex justify-center align-center class="text-xs-center">
                    <input type="file" id="csv_file" name="csv_file" class="form-controll" @change="loadCSV($event)">
                </v-flex>
                <br />
                <v-flex>
                    <v-data-table :headers="csvHeaders" v-if="showCSVTable" :items="parse_csv" :key="id" :rows-per-page-items="rowsPerPageItems3" :pagination.sync="pagination3">
                        <template slot="headerCell" slot-scope="props">
                            <v-tooltip top eager>
                            <span slot="activator" :style="$store.state.textFormatting.main">
                                {{ props.header.text }}
                            </span>
                            <span :style="$store.state.textFormatting.main">
                                {{ props.header.ad_text }}
                            </span>
                            </v-tooltip>
                        </template>
                        <template slot="items" slot-scope="props">
                            <tr>
                                    <td class="text-xs-center" :style="$store.state.textFormatting.main"> {{ props.item.alertname }} </td>
                                    <td class="text-xs-center" :style="$store.state.textFormatting.main"> <div style="text-align:right">
                                        Assertions : <v-icon v-if="props.item.assertion"> check </v-icon><v-icon v-if="!props.item.assertion"> clear </v-icon>(<v-icon v-if="props.item.assertion_emails"> check </v-icon><v-icon v-if="!props.item.assertion_emails"> clear </v-icon>)<br >
                                        Path Changes :  <v-icon v-if="props.item.pathchange"> check </v-icon><v-icon v-if="!props.item.pathchange"> clear </v-icon>(<v-icon v-if="props.item.pathchange_emails"> check </v-icon><v-icon v-if="!props.item.pathchange_emails"> clear </v-icon>)<br >
                                        First Seen :  <v-icon v-if="props.item.firstseen"> check </v-icon><v-icon v-if="!props.item.firstseen"> clear </v-icon>(<v-icon v-if="props.item.firstseen_emails"> check </v-icon><v-icon v-if="!props.item.firstseen_emails"> clear </v-icon>)<br ></div>
                                         </td>
                                    <td class="text-xs-center" :style="$store.state.textFormatting.main">
                                        <span v-for="origin in props.item.origin_is">{{ origin }}<br /></span></td>
                                    <td class="text-xs-center" :style="$store.state.textFormatting.main">
                                        <span v-for="prefix in props.item.prefix_is">{{ prefix }}<br /></span></td>
                                    <td class="text-xs-center" :style="$store.state.textFormatting.main">
                                        <span v-for="path in props.item.path_contains">{{ path }}<br /></span></td>

                            </tr>
                        </template>
                        <template slot="no-data">
                            <v-alert :value="true" color="success" icon="warning" :style="$store.state.textFormatting.main">
                                Choose file to populate table
                            </v-alert>
                        </template>
                    </v-data-table>
                </v-flex>
                <v-layout align-center justify-center>
                    <v-flex v-if="csvErr" class="text-xs-center"  xs10>
                        <v-card color="success">
                            <h4 v-html="csvErr" :style="$store.state.textFormatting.main"></h4>
                        </v-card>
                        <br />
                    </v-flex>
                </v-layout>

                <v-layout>
                    <v-btn v-if="!imported" color="red" class="mr-4" @click="csvClose"> Cancel <v-icon> mdi-cancel </v-icon> </v-btn>
                    <v-btn v-if="imported" color="red"  class="mr-4" @click="csvClose"> Close (completed) </v-btn>

                    <v-spacer></v-spacer>
                    <v-btn color="success" class="mr-4" @click="csvSubmit('org')" v-if="addType=='organisation'" :disabled="!valid3" :style="$store.state.textFormatting.main"> Submit <v-icon>mdi-checkbox-marked-circle</v-icon>  </v-btn>
                    <v-btn color="success" class="mr-4" @click="csvSubmit('user')" v-if="addType=='user'" :disabled="!valid3" :style="$store.state.textFormatting.main"> Submit <v-icon>mdi-checkbox-marked-circle</v-icon>  </v-btn>
                </v-layout>
            </v-card>
        </v-dialog>

        <v-dialog v-model="showPurge" width="500">
            <v-toolbar color="secondary" dark>
                <v-toolbar-title> Remove all user ASNs </v-toolbar-title> <v-spacer></v-spacer> <v-icon>edit</v-icon> 
            </v-toolbar>
            <v-card >
                <v-card-text>
                    <h3> Are you sure you want to delete all user ASNs? This is not reversible</h3>
                    <h3> It is highly recommended to download a CSV of the ASNs prior to deletion</h3>
                </v-card-text>
                <v-card-actions>
                    <v-btn :disabled="purge_loading" class="green" dark @click="purgeASNs"> Yes, delete</v-btn>
                    <v-spacer></v-spacer>
                    <v-btn class="red" dark @click="showPurge=false"> No, close </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog v-model="showAssist" width="800">
            <v-toolbar color="secondary" dark>
                <v-toolbar-title> Find ASNs and Prefixes </v-toolbar-title> <v-spacer></v-spacer> <v-icon>edit</v-icon> 
            </v-toolbar>
            <v-card >
                <v-card-text>
                    <h3> Search for related</h3>
                </v-card-text>
                <v-form ref="assistForm" v-model="valid" lazy-validation>
                    <v-layout wrap justify-center row>
                        <v-flex xs6 class="px-2">
                            <v-text-field 
                                :style="$store.state.textFormatting.main" 
                                v-model="assistForm.prefix" 
                                label="Search by ASN"></v-text-field>
                        </v-flex>
                        <v-flex xs6 class="px-2">
                            <v-text-field 
                                :style="$store.state.textFormatting.main" 
                                v-model="assistForm.asn" 
                                label="Search by Organisation"></v-text-field>
                        </v-flex>
                    </v-layout>
                </v-form>
                <v-card-actions>
                    <v-btn :disabled="purge_loading" class="green" dark @click="addValues"> Yes, delete</v-btn>
                    <v-spacer></v-spacer>
                    <v-btn class="red" dark @click="showAssist=false"> No, close </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

    </v-container>
</template>

<script>
import { Auth,API, graphqlOperation } from "aws-amplify";
import store from "../../store";
import info from "../../shared/operational/infopopout.vue";
import downloadcsv from "vue-json-csv";
import { findParentComponentByName } from "../utils/findParent.js";
import * as csv2json from 'csvtojson';

const cidrRegex = require("cidr-regex");

export default {
  name: "asnSettings",
  components: {
    info,
    downloadcsv,
  },
  icons: {
    iconfont: 'mdi'
  },
  describe:
    "This page allows Admins to edit the ASNs associated with their organisation, or ASNs they want to keep an eye on for other reasons",
  data() {
    return {
      user_type: "Analyst",
      valid: false,
      addType: "",
      loading: true,
      errcount: 0,
      warncount: 0,
      deleteASN: 0,
      deleteRule: 0,
      imported:false,
      msgColor: "success",
      asn_rules: [
        (v) => !!v || "ASN required",
        (v) => /^\d+$/.test(v) || "ASN must be number ( > 0 )",
      ],
      edType: "Add",
      showEdit: false,
      showAreYouSure: false,
      showDeleteRuleCheck: false,
      rowsPerPageItems: [10, 20, 50, 100],
      pagination: {
        rowsPerPage: 10,
      },
      rowsPerPageItems3: [10, 20, 50, 100],
      pagination3: {
        rowsPerPage: 10,
      },
      id: 1,
      organisation: "",
      v4rules: [
        (v) => !!v || "A valid IPv4 address is required",
        (v) =>
          /^(([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])\.){3}([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])(\/([8-9]|[1-2][0-9]|3[0-2]))?$/.test(
            v
          ) || "Address must be valid",
      ],
      v6rules: [
        (v) => !!v || "A valid IPv6 address required",
        (v) =>
          /^s*((([0-9A-Fa-f]{1,4}:){7}([0-9A-Fa-f]{1,4}|:))|(([0-9A-Fa-f]{1,4}:){6}(:[0-9A-Fa-f]{1,4}|((25[0-5]|2[0-4]d|1dd|[1-9]?d)(.(25[0-5]|2[0-4]d|1dd|[1-9]?d)){3})|:))|(([0-9A-Fa-f]{1,4}:){5}(((:[0-9A-Fa-f]{1,4}){1,2})|:((25[0-5]|2[0-4]d|1dd|[1-9]?d)(.(25[0-5]|2[0-4]d|1dd|[1-9]?d)){3})|:))|(([0-9A-Fa-f]{1,4}:){4}(((:[0-9A-Fa-f]{1,4}){1,3})|((:[0-9A-Fa-f]{1,4})?:((25[0-5]|2[0-4]d|1dd|[1-9]?d)(.(25[0-5]|2[0-4]d|1dd|[1-9]?d)){3}))|:))|(([0-9A-Fa-f]{1,4}:){3}(((:[0-9A-Fa-f]{1,4}){1,4})|((:[0-9A-Fa-f]{1,4}){0,2}:((25[0-5]|2[0-4]d|1dd|[1-9]?d)(.(25[0-5]|2[0-4]d|1dd|[1-9]?d)){3}))|:))|(([0-9A-Fa-f]{1,4}:){2}(((:[0-9A-Fa-f]{1,4}){1,5})|((:[0-9A-Fa-f]{1,4}){0,3}:((25[0-5]|2[0-4]d|1dd|[1-9]?d)(.(25[0-5]|2[0-4]d|1dd|[1-9]?d)){3}))|:))|(([0-9A-Fa-f]{1,4}:){1}(((:[0-9A-Fa-f]{1,4}){1,6})|((:[0-9A-Fa-f]{1,4}){0,4}:((25[0-5]|2[0-4]d|1dd|[1-9]?d)(.(25[0-5]|2[0-4]d|1dd|[1-9]?d)){3}))|:))|(:(((:[0-9A-Fa-f]{1,4}){1,7})|((:[0-9A-Fa-f]{1,4}){0,5}:((25[0-5]|2[0-4]d|1dd|[1-9]?d)(.(25[0-5]|2[0-4]d|1dd|[1-9]?d)){3}))|:)))(%.+)?s*(\/(3[2-9]|[4-9][0-9]|1[0-1][0-9]|12[0-8]))?$/.test(
            v
          ) || "Address must be valid",
      ],
      message: "",
      alertHeaders: [
        {
          text: "Group",
          ad_text: "Rule Group",
          value: "alertGroup",
          align: "centre",
        },
        {
          text: "Name",
          ad_text: "Rule Name",
          value: "alertName",
          align: "centre",
        },
        { text: "Alerts", ad_text: "Alerts", value: "alerts", align: "centre" },
        {
          text: "Origin",
          ad_text: "Origins",
          value: "origin",
          align: "centre",
        },
        {
          text: "Prefix",
          ad_text: "Prefixes",
          value: "prefix",
          align: "centre",
        },
        { text: "Path", ad_text: "Path ASNs", value: "path", align: "centre" },
        {
          text: "H",
          ad_text: "Hijack Score Threshold",
          value: "hijack",
          align: "centre",
        },
        {
          text: "I",
          ad_text: "Impact Score Threshold",
          value: "impact",
          align: "centre",
        },
        {
          text: "V",
          ad_text: "Validation Score Threshold",
          value: "repeat",
          align: "centre",
        },
        {
          text: "Notifications",
          ad_text: "Count of notifications over last week for given ASN",
          value: "notification_count",
          align: "centre",
        },
        {
          text: "Send Emails?",
          ad_text:
            "Whether the user/ organisation will receive emails based on alerts from this ASN",
          value: "emails",
          align: "centre",
        },
        {
          text: "And/ Or?",
          ad_text: "To do with scoring mechanism - see info. in top right",
          value: "and_or",
          align: "centre",
        },
        {
          text: "Actions",
          ad_text: "Edit or delete the ASN",
          align: "centre",
          sortable: false,
        },
        {
          text: "Enabled",
          ad_text: "Enable or Disable Rule",
          align: "centre",
          sortable: false,
        },
      ],
      asnHeaders: [
        {
          text: "ASN",
          ad_text: "Autonomous System Number",
          value: "asn",
          align: "centre",
        },
        {
          text: "ASN Name",
          ad_text: "User defined ASN name (for housekeeping)",
          value: "asn_name",
          align: "centre",
        },
        {
          text: "H",
          ad_text: "Hijack Score Threshold",
          value: "H",
          align: "centre",
        },
        {
          text: "I",
          ad_text: "Impact Score Threshold",
          value: "I",
          align: "centre",
        },
        {
          text: "V",
          ad_text: "Validation Score Threshold",
          value: "V",
          align: "centre",
        },
        {
          text: "Notifications",
          ad_text: "Count of notifications over last week for given ASN",
          value: "notification_count",
          align: "centre",
        },
        {
          text: "Alert Type",
          ad_text: "Either path or origin - see info. in top right",
          value: "alerting_type",
          align: "centre",
        },
        {
          text: "Ex. Peer?",
          ad_text: "Yes / No - see info. in top right",
          value: "exclude_peer",
          align: "centre",
        },
        {
          text: "Send Emails?",
          ad_text:
            "Whether the user/ organisation will receive emails based on alerts from this ASN",
          value: "emails",
          align: "centre",
        },
        {
          text: "And/ Or?",
          ad_text: "To do with scoring mechanism - see info. in top right",
          value: "and_or",
          align: "centre",
        },
        {
          text: "Actions",
          ad_text: "Edit or delete the ASN",
          align: "centre",
          sortable: false,
        },
      ],
      assistForm: {
        prefix: "",
        asns: []
      },
      ruleGroups: ['Default'],
      ruleForm: {
        rulename: "",
        assertions: false,
        assertions_email: false,
        intraorg: false,
        pathchanges: false,
        pathchanges_email: false,
        firstseens: false,
        firstseens_email: false,
        prefix_in: "",
        prefix_notin: "",
        origin_in: "",
        origin_notin: "",
        path_contains: "",
        path_notcontains: "",
        hijackscore: 0,
        impactscore: 0,
        andor: "and",
        repeatemails: 0,
        prefixes_enabled: false,
        origins_enabled: false,
        paths_enabled: false,
        overrides_hijack: false,
        overrides_impact: false,
        overrides_hijack_score:0,
        overrides_impact_score:0
      },
      pathPositions:[
        { name: "Anywhere", value: "any" },
        { name: "Next to Origin", value: "next" },
      ],
      originDirection:[
        { name: "New or Old origins", value: "both" },
        { name: "New origin", value: "new" },
        { name: "Old origin", value: "old" },
      ],
      form: {
        asn: 0,
        h: 0,
        i: 0,
        v: 0,
        peering: false,
        asn_name: "",
        type: 0,
        alerting_type: "origin",
        exclude_peer: true,
        emails: false,
        and_or: "and",
        as_group: null,
        opf_split: [false, false, false, false],
        opf_split_e: [false, false, false, false],
      },
      selectedASN: {
        asn: 0,
        h: 0,
        i: 0,
        v: 0,
        peering: false,
        asn_name: "",
        type: 0,
        alerting_type: "origin",
        exclude_peer: true,
        emails: false,
        and_or: "and",
        own_path_first: "1000",
        emails_opf: "0000",
        as_group: "",
      },
      alertingTypes: [
        { name: "Path based", value: "path" },
        { name: "Origin ASN based", value: "origin" },
      ],
      andOr: [
        { name: "And", value: "and" },
        { name: "Or", value: "or" },
      ],
      orgRules: [],
      userRules: [],
      orgASNs: [],
      userASNs: [],
      asnTypes: [
        { name: "Regular", value: 0 },
        { name: "High Importance", value: 1 },
        { name: "Low Importance", value: 2 },
        { name: "Muted", value: 3 },
        { name: "Show All", value: 4 },
      ],
      asnInvTypes: {
        0: "Regular",
        1: "High Importance",
        2: "Low Importance",
        3: "Muted",
        4: "Show All",
      },
      asnFlipInvTypes: {
        Regular: 0,
        "High Importance": 1,
        "Low Importance": 2,
        Muted: 3,
        "Show All": 4,
      },
      asnMappings: {
        0: { h: 30, i: 10, v: 5 },
        1: { h: 20, i: 8, v: 2 },
        2: { h: 50, i: 25, v: 10 },
        3: { h: 99, i: 99, v: 99 },
        4: { h: 0, i: 0, v: 0 },
      },
      displayMessage: false,
      username: "",
      loading2: false,
      parse_header: [],
      parse_csv: [],
      sortOrders: {},
      sortKey: "",
      showCSV: false,
      valid3: false,
      csvHeaders: [
        {
          text: "Name",
          ad_text: "Name of Rule",
          value: "alertName",
          align: "centre",
        },
        {
          text: "Group",
          ad_text: "Name of group for rule",
          value: "alertGroup",
          align: "centre",
        },
        {
          text: "Own. Assertions?",
          ad_text:
            "Whether you will be alerted upon ownership assertions for this ASN (and receive emails on such alerts)",
          value: "assertion",
          align: "centre",
        },
        {
          text: "Path Changes?",
          ad_text:
            "Whether you will be alerted on path change events for this ASN (and receive emails on such alerts)",
          value: "pathchange",
          align: "centre",
        },
        {
          text: "First Seens?",
          ad_text:
            "Whether you will be alerted upon first seen prefix events or not (and receive emails on such alerts)",
          value: "firstseen",
          align: "centre",
        },
        {
          text: "Origins",
          ad_text:
            "List of ASNs thaty will trigger an alert",
          value: "origin_is",
          align: "centre",
        },
        {
          text: "Prefixes",
          ad_text:
            "List of ASNs thaty will trigger an alert",
          value: "prefix_is",
          align: "centre",
        },
        {
          text: "ASNs in Path",
          ad_text:
            "List of ASNs thaty will trigger an alert",
          value: "path_as_contains",
          align: "centre",
        },
        {
          text: "Hijack Score",
          ad_text: "Hijack Score Threshold",
          value: "scores_hijack",
          align: "centre",
        },
        {
          text: "And/ Or?",
          ad_text: "To do with scoring mechanism - see info. in top right",
          value: "scores_andor",
          align: "centre",
        },
        {
          text: "Impact Score",
          ad_text: "Impact Score Threshold",
          value: "scores_impact",
          align: "centre",
        },
      ],
      correctHeaders: [
        'alertname',
        'alertgroup',
        'assertion',
        'firstseen',
        'pathchange',
        'assertion_emails',
        'firstseen_emails',
        'pathchange_emails',
        'repeat_emails',
        'origin_direction',
        'origin_filter_asns_in_org',
        'origin_is',
        'origin_isnot',
        'path_as_in_path',
        'path_as_contains',
        'path_as_notcontains',
        'path_as_position',
        'prefix_is',
        'prefix_isin',
        'prefix_isnot',
        'scores_hijack',
        'scores_impact',
        'scores_andor',
        'over_seth',
        'over_hscore',
        'over_seti',
        'over_iscore',
        'enabled'      ],
      csvTemplate: [
        {
          alertname: "string - no quotation marks", 
          alertgroup: "string",
          assertion: "Y/N, TRUE/FALSE, y/n or 1/0",
          firstseen: "Y/N, TRUE/FALSE, y/n or 1/0",
          pathchange: "Y/N, TRUE/FALSE, y/n or 1/0",
          assertion_emails: "Y/N, TRUE/FALSE, y/n or 1/0",
          firstseen_emails: "Y/N, TRUE/FALSE, y/n or 1/0",
          pathchange_emails: "Y/N, TRUE/FALSE, y/n or 1/0",
          repeat_emails: "number",
          origin_direction: "new/old/both",
          origin_filter_asns_in_org: "",
          origin_is: "comma delimited AS numbers",
          origin_isnot: "comma delimited AS numbers",
          path_as_in_path: "Y/N, TRUE/FALSE, y/n or 1/0",
          path_as_contains: "comma delimited AS numbers",
          path_as_notcontains: "comma delimited AS numbers",
          path_as_position: "any/next",
          prefix_is: "comma delimited Prefixes (CIDR)",
          prefix_isin: "Y/N, TRUE/FALSE, y/n or 1/0",
          prefix_isnot: "comma delimited Prefixes (CIDR)",
          scores_hijack: "0-99",
          scores_impact: "0-99",
          scores_andor: "and/or",
          over_seth: "Y/N, TRUE/FALSE, y/n or 1/0",
          over_hscore: "0-99",
          over_seti: "Y/N, TRUE/FALSE, y/n or 1/0",
          over_iscore: "0-99",
          enabled: "Y/N, TRUE/FALSE, y/n or 1/0"        },
      ],
      csvErr: null,
      showCSVTable: true,
      showOrgRules: true,
      showUserRules: true,
      showOrgASNs: true,
      showUserASNs: true,
      asn_own_dict: {},
      asn_get_owner: [],
      is_asn_owners: false,
      showAdvanced: false,
      orgASGroups: [],
      userASGroups: [],
      useASGroups: [],
      showPurge: false,
      showAssist: false,
      purge_loading: false,

      user_download_asns: [],
      organisation_download_asns: [],
      user_download_rules: [],
      organisation_download_rules: [],
    };
  },
  beforeMount() {
    let app = findParentComponentByName(this, "app");
    app.checkSignedIn();

    try {
      this.organisation = this.$store.state.user.organisation;
      this.username = this.$store.state.user.username;
    } catch (err) {
      console.log(err);
      location.reload();
    }
    this.getUserType()
    this.getRules("org");
    this.getRules("user");
//    this.getASNs();
//    this.getUserASNs();
    // this.pulASNS = this.$store.state.allASNInfoForUser;
    //var a = this.$store.state.allASNInfoForUser;
    /* for (var i=0; i< a.length; i=i+1){
             this.asn.push(a[i].asn)
        } */

    if (this.$store.state.orgASGroups) {
  //    console.log("pulling org AS groups from store");
      this.orgASGroups = this.$store.state.orgASGroups;
    } else {
  //    console.log("gathering org AS groups");
      this.getOrgGroups();
    }

    if (this.$store.state.userASGroups) {
  //    console.log("pulling user AS groups from store");
      this.userASGroups = this.$store.state.userASGroups;
    } else {
  //    console.log("gathering user AS groups");
      this.getUserGroups();
    }

    this.showAdvanced = this.$store.state.showSettings.advancedSettings;
    this.advancedChange();
  },
  methods: {
    getUserType: async function () {
            const signedin = await Auth.currentAuthenticatedUser();
            this.user_type=signedin.attributes.name;
            console.log(this.user_type);
    },
    sleep(ms) {
        return new Promise(resolve => setTimeout(resolve, ms));
    },

    addValues: async function () {

    },

    purgeASNs: async function () {
      this.purge_loading = true;
      try {
        var query =
          `
                    mutation purgeASNs {
                        purgeASNs (
                            p_user: "` +
          this.username +
          `",
                            p_organisation: "` +
          this.organisation +
          `"
                        ) {
                            response
                        }
                    }
                `;
    //    console.log(query);
        const responseObj = await API.graphql(graphqlOperation(query));
        this.purge_loading = false;
        this.showPurge = false;
        this.getUserASNs();
      } catch (e) {
    //    console.log(e);
        this.purge_loading = false;
        this.showPurge = false;
        this.getUserASNs();
      }
    },
    routeNewWindow: async function (input) {
      let routeData = this.$router.resolve({ name: input });
      window.open(routeData.href, "_blank");
    },
    getOrgGroups: async function () {
      //this.loading = true;

      var query =
        `query getASNGroups {
                getASNGroups (
                    p_organisation: "` +
        this.organisation +
        `",
                    p_user: "whole_organisation"
                ) {
                    group_name,
                    exclude,
                    hijack_setting
                }
            }`;

      const groupData = await API.graphql(graphqlOperation(query));
    //  console.log(groupData);
      var groups = groupData.data.getASNGroups;
    //  console.log(groups);

      if (!groups) {
        this.orgASGroups = [];
        this.$store.commit("setOrgASGroups", []);
        //this.loading = false
        //this.showOrgGroups = true
      } else if (groups.length == 0) {
        this.orgASGroups = [];
        this.$store.commit("setOrgASGroups", []);
        //this.loading = false
        //this.showOrgGroups = true
      } else {
        for (var i = 0; i < groups.length; i++) {
          groups[i].exclude_hij = groups[i].exclude == 1 ? "Exclude" : "Hijack"; //if 1, its hijack score based else it's exclusion based
        }
        this.orgASGroups = groups;
        this.$store.commit("setOrgASGroups", groups);
        //this.loading = false;
        //this.showOrgGroups = true
      }
    },
    getUserGroups: async function () {
      //this.loading2 = true;

      var query =
        `query getASNGroups {
                getASNGroups (
                    p_organisation: "` +
        this.organisation +
        `",
                    p_user: "` +
        this.username +
        `"
                ) {
                    group_name,
                    exclude,
                    hijack_setting
                }
            }`;

      const groupData = await API.graphql(graphqlOperation(query));
    //  console.log(groupData);
      var groups = groupData.data.getASNGroups;
    //  console.log(groups);

      if (!groups) {
        this.userASGroups = [];
        this.$store.commit("setUserASGroups", []);
        //this.loading2 = false
        //this.showUserGroups = true
      } else if (groups.length == 0) {
        this.userASGroups = [];
        this.$store.commit("setUserASGroups", []);
        //this.loading2 = false
        //this.showUserGroups = true
      } else {
        for (var i = 0; i < groups.length; i++) {
          groups[i].exclude_hij = groups[i].exclude == 1 ? "Exclude" : "Hijack"; //if 1, its hijack score based else it's exclusion based
        }
        this.userASGroups = groups;
        this.$store.commit("setUserASGroups", groups);
        //this.loading2 = false;
        //this.showUserGroups = true
      }
    },
    advancedChange: async function () {
      if (this.showAdvanced) {
        (this.alertHeaders = [
          {
            text: "Group",
            ad_text: "Rule Group",
            value: "alertGroup",
            align: "centre",
          },
          {
            text: "Name",
            ad_text: "Rule Name",
            value: "alertName",
            align: "centre",
          },
          {
            text: "Alerts",
            ad_text: "Alerts",
            value: "alerts",
            align: "centre",
          },
          {
            text: "Origin",
            ad_text: "Origins",
            value: "origin",
            align: "centre",
          },
          {
            text: "Prefix",
            ad_text: "Prefixes",
            value: "prefix",
            align: "centre",
          },
          {
            text: "Path",
            ad_text: "Path ASNs",
            value: "path",
            align: "centre",
          },
          {
            text: "H",
            ad_text: "Hijack Score Threshold",
            value: "hijack",
            align: "centre",
          },
          {
            text: "And/ Or?",
            ad_text: "To do with scoring mechanism - see info. in top right",
            value: "andor",
            align: "centre",
          },
          {
            text: "I",
            ad_text: "Impact Score Threshold",
            value: "impact",
            align: "centre",
          },
          //{ text: 'Send Emails?', ad_text:'Whether the user/ organisation will receive emails based on alerts from this ASN', value: 'emails', align:'centre'},

          {
            text: "Email Threshold (V)",
            ad_text: "Validation Score Threshold",
            value: "repeat",
            align: "centre",
          },
          {
            text: "Notifications",
            ad_text: "Count of notifications over last week for given ASN",
            value: "notification_count",
            align: "centre",
          },
          {
            text: "Actions",
            ad_text: "Edit or delete the ASN",
            align: "centre",
            sortable: false,
          },
          {
            text: "Enabled",
            ad_text: "Enable or Disable Rule",
            align: "centre",
            sortable: false,
          },
        ]),
          (this.asnHeaders = [
            {
              text: "ASN",
              ad_text: "Autonomous System Number",
              value: "asn",
              align: "centre",
            },
            {
              text: "ASN Name",
              ad_text: "User defined ASN name (for housekeeping)",
              value: "asn_name",
              align: "centre",
            },
            {
              text: "AS Group",
              ad_text:
                "Define AS groups to omit / tweak alerts that occur between certain ASNs",
              value: "as_group",
              align: "centre",
            },
            {
              text: "Own. Assertions?",
              ad_text:
                "Whether you will be alerted upon ownership assertions for this ASN (and receive emails on such alerts)",
              value: "opf_split[0]",
              align: "centre",
            },
            {
              text: "Path Changes?",
              ad_text:
                "Whether you will be alerted on path change events for this ASN (and receive emails on such alerts)",
              value: "opf_split[1]",
              align: "centre",
            },
            {
              text: "First Seens?",
              ad_text:
                "Whether you will be alerted upon first seen prefix events or not (and receive emails on such alerts)",
              value: "opf_split[2]",
              align: "centre",
            },
            {
              text: "AS In Path Alerts?",
              ad_text:
                "Whether you will be alerted upon alerts with this AS in the path - excluding this AS as the peer (and receive emails on such alerts)",
              value: "opf_split[3]",
              align: "centre",
            },
            {
              text: "H",
              ad_text: "Hijack Score Threshold",
              value: "H",
              align: "centre",
            },
            {
              text: "And/ Or?",
              ad_text: "To do with scoring mechanism - see info. in top right",
              value: "and_or",
              align: "centre",
            },
            {
              text: "I",
              ad_text: "Impact Score Threshold",
              value: "I",
              align: "centre",
            },
            //{ text: 'Alert Type', ad_text:'Either path or origin - see info. in top right', value: 'alerting_type', align:'centre'},
            //{ text: 'Ex. Peer?', ad_text:'Yes / No - see info. in top right', value: 'exclude_peer', align:'centre'},
            //{ text: 'Send Emails?', ad_text:'Whether the user/ organisation will receive emails based on alerts from this ASN', value: 'emails', align:'centre'},

            {
              text: "Email Threshold (V)",
              ad_text:
                "The amount of events required for an additional email alert to be sent out",
              value: "V",
              align: "centre",
            },
            {
              text: "Notifications",
              ad_text: "Count of notifications over last week for given ASN",
              value: "notification_count",
              align: "centre",
            },
            {
              text: "Actions",
              ad_text: "Edit or delete the ASN",
              align: "centre",
              sortable: false,
            },
          ]);
      } else {
        (this.alertHeaders = [
          {
            text: "Group",
            ad_text: "Rule Group",
            value: "alertName",
            align: "centre",
          },
          {
            text: "Name",
            ad_text: "Rule Name",
            value: "alertName",
            align: "centre",
          },
          {
            text: "Alerts",
            ad_text: "Alerts",
            value: "alerts",
            align: "centre",
          },
          {
            text: "Origin",
            ad_text: "Origin conditions",
            value: "origin",
            align: "centre",
          },
          {
            text: "Prefix",
            ad_text: "Prefix conditions",
            value: "prefix",
            align: "centre",
          },
          {
            text: "Path",
            ad_text: "Path conditions",
            value: "path",
            align: "centre",
          },
          //{ text: 'H', ad_text:'Hijack Score Threshold', value: 'H', align:'centre'},
          //{ text: 'I', ad_text:'Impact Score Threshold', value: 'I', align:'centre'},
          //{ text: 'V', ad_text:'Validation Score Threshold', value: 'V', align:'centre'},
          //{ text: 'Send Emails?', ad_text:'Whether the user/ organisation will receive emails based on alerts from this ASN', value: 'emails', align:'centre'},
          //{ text: 'And/ Or?', ad_text:'To do with scoring mechanism - see info. in top right', value: 'and_or', align:'centre'},
          {
            text: "Notifications",
            ad_text: "Count of notifications over last week for given ASN",
            value: "notification_count",
            align: "centre",
          },
          {
            text: "Actions",
            ad_text: "Edit or delete the ASN",
            align: "centre",
            sortable: false,
          },
          {
            text: "Enabled",
            ad_text: "Enable or Disable Rule",
            align: "centre",
            sortable: false,
          },
        ]),
          (this.asnHeaders = [
            {
              text: "ASN",
              ad_text: "Autonomous System Number",
              value: "asn",
              align: "centre",
            },
            {
              text: "ASN Name",
              ad_text: "User defined ASN name (for housekeeping)",
              value: "asn_name",
              align: "centre",
            },
            //{ text: 'AS Group', ad_text:'Define AS groups to omit / tweak alerts that occur between certain ASNs', value: 'as_group', align:'centre'},
            //{ text: 'Own. Assertions?', ad_text:'Whether you will be alerted upon ownership assertions for this ASN (and receive emails on such alerts)', value: 'opf_split[0]', align:'centre'},
            //{ text: 'Path Changes?', ad_text:'Whether you will be alerted on path change events for this ASN (and receive emails on such alerts)', value: 'opf_split[1]', align:'centre'},
            //{ text: 'First Seens?', ad_text:'Whether you will be alerted upon first seen prefix events or not (and receive emails on such alerts)', value: 'opf_split[2]', align:'centre'},
            //{ text: 'AS In Path Alerts?', ad_text:'Whether you will be alerted upon alerts with this AS in the path - excluding this AS as the peer (and receive emails on such alerts)', value: 'opf_split[3]', align:'centre'},
            //{ text: 'H', ad_text:'Hijack Score Threshold', value: 'H', align:'centre'},
            //{ text: 'I', ad_text:'Impact Score Threshold', value: 'I', align:'centre'},
            //{ text: 'Alert Type', ad_text:'Either path or origin - see info. in top right', value: 'alerting_type', align:'centre'},
            //{ text: 'Ex. Peer?', ad_text:'Yes / No - see info. in top right', value: 'exclude_peer', align:'centre'},
            //{ text: 'Send Emails?', ad_text:'Whether the user/ organisation will receive emails based on alerts from this ASN', value: 'emails', align:'centre'},
            //{ text: 'And/ Or?', ad_text:'To do with scoring mechanism - see info. in top right', value: 'and_or', align:'centre'},
            //{ text: 'V', ad_text:'Validation Score Threshold', value: 'V', align:'centre'},
            {
              text: "Notifications",
              ad_text: "Count of notifications over last week for given ASN",
              value: "notification_count",
              align: "centre",
            },
            {
              text: "Actions",
              ad_text: "Edit or delete the ASN",
              align: "centre",
              sortable: false,
            },
            {
              text: "Enabled",
              ad_text: "Enable or Disable Rule",
              align: "centre",
              sortable: false,
            },
          ]);
      }
    },
    csvOpen: function () {
      this.imported = false;
      this.parse_header = [];
      this.parse_csv = [];
      this.valid3 = false;
      this.csvErr = null;
      this.showCSV = true;
      this.showCSVTable = true;
    },
    csvClose: function () {
      this.showCSV = false;
    },
    csvSubmit: async function (type) {
      this.valid3 = false;
      var verb=""
      for (var i = 0; i < this.parse_csv.length; i++) {
        var entry = this.parse_csv[i];
        verb="";
        if (type=="user") {
          verb=""+entry.importType+"RulesForUser";
        }
        if (type=="org") {
          verb=""+entry.importType+"RulesForOrg";
        }

        var newrule=`mutation `+verb+`{ 
          `+verb+`(
              id:`+ entry.id +`,
              alertname: "`+entry.alertname+`",
              alertgroup: "`+entry.alertgroup+`",
              alerts: {
                assertion: `+entry.assertion+`,
                pathchange: `+entry.pathchange+`,
                firstseen: `+entry.firstseen+`
              },
              emails: {
                assertion: `+entry.assertion_emails+`,
                pathchange: `+entry.pathchange_emails+`,
                firstseen: `+entry.firstseen_emails+`,
                repeat: `+entry.repeat_emails+`
              },
              overrides: {
                sethijack: `+entry.over_seth+`,
                hijackscore: `+entry.over_hscore+`,
                setimpact: `+entry.over_seti+`,
                impactscore: `+entry.over_iscore+`
              },
              filter: {
                prefix: {
                  isin: `+entry.prefix_isin+`,
                  is: [`+entry.prefix_is+`],
                  isnot: [`+entry.prefix_isnot+`]
                },
                origin: {
                  direction: "`+entry.origin_direction+`",
                  is: [`+entry.origin_is+`],
                  isnot: [`+entry.origin_isnot+`],
                  filter_asns_in_org: `+entry.origin_filter_asns_in_org+`
                },
                path: {
                  contains: [`+entry.path_as_contains+`],
                  notcontains: [`+entry.path_as_notcontains+`],
                  position: "`+entry.path_as_position+`",
                  peer: "any",
                  pathchange: true
                },
                scores: {
                  hijack: `+entry.scores_hijack+`,
                  impact: `+entry.scores_impact+`,
                  andor: "`+entry.scores_andor+`"
                }
              },
              enabled: `+entry.enabled+`
          )
          {
            response
          }
        }`;

      //  console.log(newrule);
        var importRuleResult="";
        try {
          importRuleResult = await API.graphql(graphqlOperation(newrule));
        } catch(err) {
      //    console.log(err);
          t_msg = "There was a problem import the rules";
          this.showCSVTable = false;
        }

      //  console.log(importRuleResult);
        var responseMessage=false;
        if (type=="user") {
          if (entry.importType=="update") {
            responseMessage = importRuleResult.data.updateRulesForUser;
          } else{
            responseMessage = importRuleResult.data.addRulesForUser;
          }
        }
        if (type=="org") {
          if (entry.importType=="update") {
            responseMessage = importRuleResult.data.updateRulesForOrg;
          } else{
            responseMessage = importRuleResult.data.addRulesForOrg;
          }
        }
        var t_msg=""
        // EDIT THIS
        await this.sleep(1000);
        if (responseMessage) {
          t_msg = "Successfully Imported Rules";
          this.showCSVTable = false;
          this.shrinkEditWindow();
          if (i == this.parse_csv.length - 1) {
            if (this.addType == "user") {
              this.getRules("user");
            } else {
              this.getRules("org");
            }
          }
        } else {
          t_msg = "Error Importing Rule " + entry.alertname;
        }
        this.csvErr = t_msg;
      }
      this.imported=true;
    },
    testString(reason, name, teststr, must_be_populated, options, def_value) {
      if(/[^A-Za-z0-9 -]/.test(teststr)) {
        reason+=" - "+name+" has invalid characters<br>"
      }
      if (teststr.length==0) {
        if (must_be_populated) {
          reason+=" - "+name+" is empty"
          this.errcount+=1
        } else {
          this.warncount+=1
        }
        teststr=def_value;
      }
      if (options.length>0) {
        if (options.indexOf(teststr)<0) {
          reason+=" - "+name+" is not a valid option<br>"
          this.errcount+=1
        }
      }
      return [reason,teststr]
    },
    returnTrueFalse(reason,name,teststr, must_be_populated,def_value) {
      var result=false;
      teststr=teststr.toLowerCase();
      if (teststr=='y' || teststr=='true' || teststr=='1') {
        result=true;
      } else if (teststr=='n' || teststr=='false' || teststr=='0') {
        result=false;
      } else {
        if (must_be_populated) {
          reason+=" - ["+name+"] is not a valid boolean option<br>"
          this.errcount+=1;
        } else {
          this.warncount+=1;
        }
        result=def_value;
      }
      return [reason,result];
    },
    returnNumber(reason,name,teststr, min, max,must_be_populated, def_value) {
      var result=0;
      try {
        result=parseInt(teststr, 10);
        if (isNaN(result)) {
          result=def_value;
          if (must_be_populated) {
            reason+=" - ["+name+"] is not a valid number<br />"
            this.errcount+=1;
          }
        }
      } catch {
        result=def_value;
        if (must_be_populated) {
          reason+=" - ["+name+"] is not a valid number<br />"
          this.errcount+=1;
        }
      }
      if (result>max) {
        this.errcount+=1;
        reason+=" - ["+name+"] is too large<br />";
      }
      if (result<min) {
        this.errcount+=1;
        reason+=" - ["+name+"] is too small<br />";
      }
      return [reason,result];
    },
    getListArr: function(listString,isStr) {
      var arr=[];
      var s="";
      for ( s of listString.split(",")) {
        s=s.replace(/\s+/g, '');
        if (s.length>0) {
          if (isStr) {
            arr.push(`"`+s+`"`);
          } else {
            arr.push(s);
          }
        }
      }
      return arr;
    },
    testList(reason,name, teststr,listtype) {
      var newlist=[];
      if (listtype=="asn") {
        if (!this.validate_asnlist(teststr)) {
          reason+=" - [" + name + "] contains a non-numeric value<br />"
          this.errcount+=1;
        } else {
          newlist=this.getListArr(teststr,false)
        }
      }
      if (listtype=="cidr") {
        if (!this.validate_cidrlist(teststr)) {
          reason+=" - [" + name + "] contains an invalid prefix<br />"
          this.errcount+=1;
        } else {
          newlist=this.getListArr(teststr,true)
        }
      }
      return [reason,newlist]
    },
    testCSVInput: function () {
      var reason="";
      var entry;
      var entriestowrite=[];
      var warning='';
      var reasons='';
      this.errtotal=0;
      this.warncount=0;
      var as_group_list = [];
      var j=0;

      if (this.addType == "user") {
        for (j = 0; j < this.userASGroups.length; j++) {
          as_group_list.push(this.userASGroups[j].group_name);
        }
      } else {
        for (j = 0; j < this.orgASGroups.length; j++) {
          as_group_list.push(this.orgASGroups[j].group_name);
        }
      }
    //  console.log(as_group_list);

    //  console.log("starting CSV Input validation...");
      for (var i = 0; i < this.parse_csv.length; i++) {
        reason='';
        this.errcount=0;
        entry=this.parse_csv[i];
        if (entry.alertname!=null) {
          [reason,entry.alertname] = this.testString(reason,"Alert Name",entry.alertname,true,[],""); 
          [reason,entry.alertgroup] = this.testString(reason,"Alert Group",entry.alertgroup,false,[],"default"); 
          [reason,entry.assertion] =this.returnTrueFalse(reason,"Assertion",entry.assertion, true, false);
          [reason,entry.firstseen] =this.returnTrueFalse(reason,"First Seen",entry.firstseen, true, false);
          [reason,entry.pathchange] =this.returnTrueFalse(reason,"Path Change",entry.pathchange, true, false);
          [reason,entry.assertion_emails] =this.returnTrueFalse(reason,"Assertion Emails",entry.assertion_emails, false, false);
          [reason,entry.firstseen_emails] =this.returnTrueFalse(reason,"First Seen Emails",entry.firstseen_emails, false, false);
          [reason,entry.pathchange_emails] =this.returnTrueFalse(reason,"Path Change Emails",entry.pathchange_emails, false, false);
          [reason,entry.repeat_emails] =this.returnNumber(reason,"Email Repeat",entry.repeat_emails,0,99,false,0);
          [reason,entry.origin_direction] = this.testString(reason,"origin direction", entry.origin_direction,true,["new","old","both"],"both"); 
          [reason,entry.origin_filter_asns_in_org] =this.returnTrueFalse(reason,"Intra-Org ASNs",entry.origin_filter_asns_in_org, false, true);
          [reason,entry.origin_is] = this.testList(reason,"Origin is", entry.origin_is,"asn");
          [reason,entry.origin_isnot] = this.testList(reason,"Origin not", entry.origin_isnot,"asn");
          [reason,entry.path_as_in_path]=this.returnTrueFalse(reason,"ASN in path",entry.path_as_in_path, false, false);
          [reason,entry.path_as_contains] = this.testList(reason,"AS Path Contains", entry.path_as_contains,"asn");
          [reason,entry.path_as_notcontains] = this.testList(reason,"AS Path NotContains", entry.path_as_notcontains,"asn");
          [reason,entry.path_as_position] = this.testString(reason,"Path Position", entry.path_as_position,false,["any","next"],"any"); 
          [reason,entry.prefix_isin]=this.returnTrueFalse(reason,"Prefix is in (subnets)",entry.prefix_isin, false, false);
          [reason,entry.prefix_is] = this.testList(reason,"Prefix Is", entry.prefix_is,"cidr");
          [reason,entry.prefix_isnot] = this.testList(reason,"Prefix Is Not", entry.prefix_isnot,"cidr");
          [reason,entry.scores_hijack] =this.returnNumber(reason,"Hijack Score",entry.scores_hijack,0,99,true,0);
          [reason,entry.scores_impact] =this.returnNumber(reason,"Impact Score",entry.scores_impact,0,99,true,0);
          [reason,entry.scores_andor] = this.testString(reason,"Score And/Or", entry.scores_andor,false,["and","or"],"and"); 
          [reason,entry.over_seth] =this.returnTrueFalse(reason,"Set Hijack Score Override",entry.over_seth, false, false);
          [reason,entry.over_hscore] =this.returnNumber(reason,"Impact Score",entry.over_hscore,0,99,false,0);
          [reason,entry.over_seti] =this.returnTrueFalse(reason,"Set Impact Score Override",entry.over_seti, false, false);
          [reason,entry.over_iscore] =this.returnNumber(reason,"Impact Score",entry.scores_impact,0,99,false,0);
          [reason,entry.enabled] =this.returnTrueFalse(reason,"Enabled",entry.enabled, false, false);
        } else {
          [reason,entry.assertion] =this.returnTrueFalse(reason,"Assertion",entry.own_assertions, true, false);
          [reason,entry.firstseen] =this.returnTrueFalse(reason,"First Seen",entry.first_seens, true, false);
          [reason,entry.pathchange] =this.returnTrueFalse(reason,"Path Change",entry.path_changes, true, false);
          [reason,entry.assertion_emails] =this.returnTrueFalse(reason,"Assertion Emails",entry.own_assertions_emails, false, false);
          [reason,entry.firstseen_emails] =this.returnTrueFalse(reason,"First Seen Emails",entry.first_seens_emails, false, false);
          [reason,entry.pathchange_emails] =this.returnTrueFalse(reason,"Path Change Emails",entry.path_changes_emails, false, false);
          [reason,entry.origin_is] = this.testList(reason,"Origin is", entry.asn,"asn");
          [reason,entry.origin_filter_asns_in_org] =this.returnTrueFalse(reason,"Intra-Org ASNs",entry.intra_org_assertions, false, true);

          [reason,entry.origin_direction] = this.testString(reason,"origin direction", "both",true,["new","old","both"],"both"); 
          [reason,entry.origin_isnot] = this.testList(reason,"Origin not", "","asn");
          [reason,entry.path_as_notcontains] = this.testList(reason,"AS Path NotContains", "","asn");
          [reason,entry.path_as_position] = this.testString(reason,"Path Position", "any",false,["any","next"],"any"); 
          [reason,entry.prefix_isin]=this.returnTrueFalse(reason,"Prefix is in (subnets)","n", false, false);
          [reason,entry.prefix_is] = this.testList(reason,"Prefix Is", "","cidr");
          [reason,entry.prefix_isnot] = this.testList(reason,"Prefix Is Not", "","cidr");
          [reason,entry.over_seth] =this.returnTrueFalse(reason,"Set Hijack Score Override","n", false, false);
          [reason,entry.over_hscore] =this.returnNumber(reason,"Hijack Score",0,0,99,false,0);
          [reason,entry.enabled] =this.returnTrueFalse(reason,"Enabled","y", false, false);

          if (entry.asn_name !=null) {
            [reason,entry.alertname] = this.testString(reason,"Alert Name",entry.asn_name,true,[],""); 
            [reason,entry.alertgroup] = this.testString(reason,"Alert Group",entry.as_group,false,[],"default"); 
            [reason,entry.repeat_emails] =this.returnNumber(reason,"Email Repeat",entry.v,0,99,false,0);
            [reason,entry.scores_hijack] =this.returnNumber(reason,"Hijack Score",entry.h,0,99,true,0);
            [reason,entry.scores_impact] =this.returnNumber(reason,"Impact Score",entry.i,0,99,true,0);
            [reason,entry.scores_andor] = this.testString(reason,"Score And/Or", entry.and_or,false,["and","or"],"and"); 
            [reason,entry.path_as_in_path]=this.returnTrueFalse(reason,"ASN in path",entry.as_in_path_alerts, false, false);
            [reason,entry.path_as_contains] = this.testList(reason,"AS Path Contains", entry.asn,"asn");
            [reason,entry.over_seti] =this.returnTrueFalse(reason,"Set Impact Score Override","n", false, false);
            [reason,entry.over_iscore] =this.returnNumber(reason,"Impact Score",0,0,99,false,0);
          }
          if (entry.ip_prefix !=null) {
      //      console.log("adding prefix rule");
            [reason,entry.alertname] = this.testString(reason,"Alert Name",entry.hostname+" - "+entry.description,true,[],""); 
            [reason,entry.alertgroup] = this.testString(reason,"Alert Group","default",false,[],"default"); 
            [reason,entry.over_seti] =this.returnTrueFalse(reason,"Set Impact Score Override","y", false, false);
            [reason,entry.over_iscore] =this.returnNumber(reason,"Impact Score",entry.impact_setting,0,99,false,0);
            [reason,entry.repeat_emails] =this.returnNumber(reason,"Email Repeat",0,0,99,false,0);
            [reason,entry.scores_hijack] =this.returnNumber(reason,"Hijack Score",0,0,99,true,0);
            [reason,entry.scores_impact] =this.returnNumber(reason,"Impact Score",0,0,99,true,0);
            [reason,entry.scores_andor] = this.testString(reason,"Score And/Or", "and",false,["and","or"],"and"); 
            [reason,entry.path_as_in_path]=this.returnTrueFalse(reason,"ASN in path",'n', false, false);
            [reason,entry.path_as_contains] = this.testList(reason,"AS Path Contains", "","asn");
          }
          
        }
        if (this.errcount==0) {
          entry.importType="add";
          entry.id=0;
          var k=0
          if (this.addType=="user") {
            for (k = 0; k < this.userRules.length; k++) {
              if (entry.alertname==this.userRules[k].alertname) {
                warning+=" Rule ["+entry.alertname+"] already exists and will be overwritten.<br />"
                this.warncount+=1;
                entry.importType="update";
                entry.id=this.userRules[k].id;
              }
            }
          }
          if (this.addType=="org") {
            for (k = 0; k < this.orgRules.length; k++) {
              if (entry.alertname==this.orgRules[k].alertname) {
                warning+=" Rule ["+entry.alertname+"] already exists and will be overwritten.<br />"
                this.warncount+=1;
                entry.importType="update";
                entry.id=this.orgRules[k].id;
              }
            }
          }
          entriestowrite.push(entry);
        } else {
          reason="Entry "+i.toString()+" will NOT be imported <br />"+reason;
        }
        this.errtotal+=this.errcount;
        reasons+=reason;
      }
      this.parse_csv=entriestowrite;
      this.showCSVTable = true;
      if (reasons != "") {
        this.csvErr = reasons;
      } else {
        this.csvErr = "All entries will be written";
        if (this.warncount>0) {
          this.csvErr+="<br />WARNINGS<br />"+warning;
        }
      }
      if (this.parse_csv.length > 0) {
        this.valid3 = true;
      }
    },
    sortBy: function (key) {
      var vm = this;
      vm.sortKey = key;
      vm.sortOrders[key] = vm.sortOrders[key] * -1;
    },
    csvJSON: async function (csv) {
      var result=[];
      result=await csv2json({noheader:false,output: "json"}).fromString(csv)
    //  console.log(result);
      return result; // JavaScript object
    },
    loadCSV(e) {
      var vm = this;
      vm.valid3 = false;
    //  console.log("reading csv...");
      if (window.FileReader) {
        var reader = new FileReader();
        reader.readAsText(e.target.files[0]);
        // Handle errors load
        reader.onload = async function (event) {
          var csv = event.target.result;
          try {
            vm.parse_csv = await vm.csvJSON(csv);
          } catch (e) {
            console.log(e);
          }
      //    console.log(vm.parse_csv.length);
          if (vm.parse_csv.length > 0) {
      //      console.log("testing CSV input...");
            vm.testCSVInput();
          }
      //    console.log("finished test");
        };
        reader.onerror = function (evt) {
          if (evt.target.error.name == "NotReadableError") {
            alert("Cannot read file!");
          }
        };
      } else {
        alert("FileReader is not supported in this browser.");
      }
    },
    checkValue: function (varcheck,def) {
      if (typeof varcheck == 'undefined') {
        varcheck=def;
      }
      return varcheck;
    },
    clickRule: function (rule, scope, mode) {
      if (mode == "add") {
        this.edType = "Add";
        this.addType=scope;

        this.ruleForm.ruletype=scope;

        this.ruleForm.Groups=["Default"];
        this.ruleForm.id =0;
        this.ruleForm.alertname="";
        this.ruleForm.alertgroup="Default";

        this.ruleForm.assertions=true;
        this.ruleForm.intraorg=false;
        this.ruleForm.assertions_email=false;
 
        this.ruleForm.pathchanges=false;
        this.ruleForm.pathchanges_email=false;

        this.ruleForm.firstseens=false;
        this.ruleForm.firstseens_email=false;

        this.ruleForm.repeat=1;
        this.ruleForm.hijackscore=50;
        this.ruleForm.impactscore=50;
        this.ruleForm.andor="or";

        this.ruleForm.overrides_hijack=false;
        this.ruleForm.overrides_hijack_score=0;
        this.ruleForm.overrides_impact=false;
        this.ruleForm.overrides_impact_score=0;

        this.ruleForm.origins_enabled=true;
        this.ruleForm.origins_in="";
        this.ruleForm.origins_notin="";
        this.ruleForm.origins_direction="both";

        this.ruleForm.prefixes_enabled=false;
        this.ruleForm.prefix_in="";
        this.ruleForm.prefix_notin="";
        this.ruleForm.include_subnets=false;

        this.ruleForm.paths_enabled=false;
        this.ruleForm.path_contains="";
        this.ruleForm.path_notcontains="";
        this.ruleForm.path_position="any";
        this.ruleForm.path_excludepeer=true;

        
      }
      if (mode == "edit") {
        this.edType = "Edit";
        this.addType=scope;
        
        this.ruleForm.ruletype=rule.ruletype;

        this.ruleForm.Groups=this.ruleGroups
        this.ruleForm.id =rule.id;
        this.ruleForm.alertname=rule.alertname;
        this.ruleForm.alertgroup=rule.alertgroup;

        this.ruleForm.assertions=rule.alerts.assertion;
        this.ruleForm.intraorg=rule.filter.origin.filter_asns_in_org
        this.ruleForm.assertions_email=rule.emails.assertion;
 
        this.ruleForm.pathchanges=rule.alerts.pathchange;
        this.ruleForm.pathchanges_email=rule.emails.pathchange;

        this.ruleForm.firstseens=rule.alerts.firstseen;
        this.ruleForm.firstseens_email=rule.emails.firstseen;

        this.ruleForm.repeat=rule.emails.repeat;
        this.ruleForm.hijackscore=rule.filter.scores.hijack;
        this.ruleForm.impactscore=rule.filter.scores.impact;
        this.ruleForm.andor=rule.filter.scores.andor;

        this.ruleForm.overrides_hijack=this.checkValue(rule.overrides.sethijack,false);
        this.ruleForm.overrides_hijack_score=this.checkValue(rule.overrides.hijackscore,0);
        this.ruleForm.overrides_impact=this.checkValue(rule.overrides.setimpact,false);
        this.ruleForm.overrides_impact_score=this.checkValue(rule.overrides.impactscore,0);

        this.ruleForm.origins_enabled=false;
        if (rule.filter.origin.is.length>0 || rule.filter.origin.isnot.length>0) {
          this.ruleForm.origins_enabled=true;
          this.ruleForm.origins_in=rule.filter.origin.isfull.join("\n");
          this.ruleForm.origins_notin=rule.filter.origin.isnotfull.join("\n");
          this.ruleForm.origins_direction={value: rule.filter.origin.direction};
        } else {
          this.ruleForm.origins_in="";
          this.ruleForm.origins_notin="";
          this.ruleForm.origins_direction="both";
        }

        this.ruleForm.prefixes_enabled=false;
        if (rule.filter.prefix.is.length>0 || rule.filter.prefix.isnot.length>0) {
          this.ruleForm.prefixes_enabled=true;
          this.ruleForm.prefix_in=rule.filter.prefix.isfull.join("\n");
          this.ruleForm.prefix_notin=rule.filter.prefix.isnotfull.join("\n");
          this.ruleForm.include_subnets=rule.filter.prefix.isin
        } else {
          this.ruleForm.prefix_in="";
          this.ruleForm.prefix_notin="";
          this.ruleForm.include_subnets=false;
        }

        this.ruleForm.paths_enabled=false;
        if (rule.filter.path.contains.length>0 || rule.filter.path.notcontains.length>0) {
          this.ruleForm.paths_enabled=true;
          this.ruleForm.path_contains=rule.filter.path.containsfull.join("\n");
          this.ruleForm.path_notcontains=rule.filter.path.notcontainsfull.join("\n");
          this.ruleForm.path_position={value: rule.filter.path.position};
          this.ruleForm.path_excludepeer=rule.filter.path.exclude_peer;
        } else {
          this.ruleForm.path_contains="";
          this.ruleForm.path_notcontains="";
          this.ruleForm.path_position="any";
          this.ruleForm.path_excludepeer=true;
        }
      }
      if (mode == "edit" || mode == "add") {
        this.showEdit = true;
        window.scrollTo(
          0,
          document.body.scrollHeight || document.documentElement.scrollHeight
        );
        this.$nextTick(() => {
          // Add the component back in
          window.scrollTo(
            0,
            document.body.scrollHeight || document.documentElement.scrollHeight
          );
        });
      }
    },
    validate_cidrlist: function (cidrlist) {
      var valid=true;
      var acidr='';
      if (cidrlist.length>1){
        for (acidr of cidrlist.split(",")) {
          acidr=acidr.replace(/['"]+/g, '');
          if (!cidrRegex({exact:true}).test(acidr)) {
            if (!cidrRegex.v6({exact:true}).test(acidr)) {
              valid=false; }
          }
        }
      }
      return valid;
    },
    validate_asnlist: function (asnlist) {
      var valid=true;
      var asn='';
      if (asnlist.length>1) {
        for (asn of asnlist.split(",")) {
          asn=asn.replace(/['"]+/g, '');
          if (isNaN(parseInt(asn)) || !isFinite(asn)) {
              valid=false; 
          }
        }
      }
      return valid;
    },
    addRule: async function () {
      var valid=true;
      var invaliddetails="";
      if (!this.ruleForm.assertions) {this.ruleForm.assertions_email=false;}
      if (!this.ruleForm.pathchanges) {this.ruleForm.pathchanges_email=false;}
      if (!this.ruleForm.firstseens) {this.ruleForm.firstseens_email=false;}
      var p_in=this.getList(this.ruleForm.prefix_in,true);
      var p_notin=this.getList(this.ruleForm.prefix_notin,true);
      if ((p_in.length==0 && p_notin.length==0) || !this.ruleForm.prefixes_enabled) {
        p_in="";
        p_notin="";
      }
      var o_in=this.getList(this.ruleForm.origins_in,false);
      var o_notin=this.getList(this.ruleForm.origins_notin,false);
      if ((o_in.length==0 && o_notin.length==0) || !this.ruleForm.origins_enabled) {
        o_in="";
        o_notin="";
      }
      var pa_in=this.getList(this.ruleForm.path_contains,false);
      var pa_notin=this.getList(this.ruleForm.path_notcontains,false);
      if ((pa_in.length==0 && pa_notin.length==0) || !this.ruleForm.paths_enabled) {
        pa_in="";
        pa_notin="";
      }
      var k=0;
      if (this.addType=="org") {
        for (k = 0; k < this.userRules.length; k++) {        
          if (this.userRules[k].alertname.toLowerCase() == this.ruleForm.alertname.toLowerCase()) {
            valid=false;
            invaliddetails+="Rule Name already exists in your Organisation Rules";
          }
        }
      }
      if (this.addType=="user") {
        for (k = 0; k < this.userRules.length; k++) {        
          if (this.userRules[k].alertname.toLowerCase() == this.ruleForm.alertname.toLowerCase()) {
            valid=false;
            invaliddetails+="Rule Name already exists in your User Rules";
          }
        }
      }
      if (this.ruleForm.alertname.length<3) {
        valid=false;
        invaliddetails+="Rule Name is empty or not long enough (min 3 characters)"
      }
      if (!this.validate_cidrlist(p_in) || !this.validate_cidrlist(p_notin)) {
        valid=false;
        invaliddetails+="Invalid Prefix in prefix list";
      }
      if (!this.validate_asnlist(o_in) || !this.validate_asnlist(o_notin)) {
        valid=false;
        invaliddetails+="Invalid asn in origin list";
      }
      if (!this.validate_asnlist(pa_in) || !this.validate_asnlist(pa_notin)) {
        valid=false;
        invaliddetails+="Invalid asn in path list";
      }
    //  console.log(valid)
      var newrule='';
      if (valid) {
        if (this.addType=='org') {

          newrule=`mutation addRules{ 
            addRulesForOrg(
                id:`+ this.ruleForm.id +`,
                alertname: "`+this.ruleForm.alertname+`",
                alertgroup: "`+this.ruleForm.alertgroup+`",
                alerts: {
                  assertion: `+this.ruleForm.assertions+`,
                  pathchange: `+this.ruleForm.pathchanges+`,
                  firstseen: `+this.ruleForm.firstseens+`
                },
                emails: {
                  assertion: `+this.ruleForm.assertions_email+`,
                  pathchange: `+this.ruleForm.pathchanges_email+`,
                  firstseen: `+this.ruleForm.firstseens_email+`,
                  repeat: `+this.ruleForm.repeat+`
                },
                overrides: {
                  sethijack: `+this.ruleForm.overrides_hijack+`,
                  hijackscore: `+this.ruleForm.overrides_hijack_score+`,
                  setimpact: `+this.ruleForm.overrides_impact+`,
                  impactscore: `+this.ruleForm.overrides_impact_score+`
                },
                filter: {
                  prefix: {
                    isin: `+this.ruleForm.include_subnets+`,
                    is: [`+p_in+`],
                    isnot: [`+p_notin+`]
                  },
                  origin: {
                    direction: "`+this.ruleForm.origins_direction+`",
                    is: [`+o_in+`],
                    isnot: [`+o_notin+`],
                    filter_asns_in_org: `+this.ruleForm.intraorg+`
                  },
                  path: {
                    contains: [`+pa_in+`],
                    notcontains: [`+pa_notin+`],
                    position: "`+this.ruleForm.path_position+`",
                    peer: "any",
                    pathchange: true
                  },
                  scores: {
                    hijack: `+this.ruleForm.hijackscore+`,
                    impact: `+this.ruleForm.impactscore+`,
                    andor: "`+this.ruleForm.andor+`"
                  }
                },
                enabled: true
            )
            {
              response
            }
          }`;
        }
        if (this.addType=='user') {

          newrule=`mutation addRules{ 
            addRulesForUser(
                id:`+ this.ruleForm.id +`,
                alertname: "`+this.ruleForm.alertname+`",
                alertgroup: "`+this.ruleForm.alertgroup+`",
                alerts: {
                  assertion: `+this.ruleForm.assertions+`,
                  pathchange: `+this.ruleForm.pathchanges+`,
                  firstseen: `+this.ruleForm.firstseens+`
                },
                emails: {
                  assertion: `+this.ruleForm.assertions_email+`,
                  pathchange: `+this.ruleForm.pathchanges_email+`,
                  firstseen: `+this.ruleForm.firstseens_email+`,
                  repeat: `+this.ruleForm.repeat+`
                },
                overrides: {
                  sethijack: `+this.ruleForm.overrides_hijack+`,
                  hijackscore: `+this.ruleForm.overrides_hijack_score+`,
                  setimpact: `+this.ruleForm.overrides_impact+`,
                  impactscore: `+this.ruleForm.overrides_impact_score+`
                },
                filter: {
                  prefix: {
                    isin: `+this.ruleForm.include_subnets+`,
                    is: [`+p_in+`],
                    isnot: [`+p_notin+`]
                  },
                  origin: {
                    direction: "`+this.ruleForm.origins_direction+`",
                    is: [`+o_in+`],
                    isnot: [`+o_notin+`],
                    filter_asns_in_org: `+this.ruleForm.intraorg+`
                  },
                  path: {
                    contains: [`+pa_in+`],
                    notcontains: [`+pa_notin+`],
                    position: "`+this.ruleForm.path_position+`",
                    peer: "any",
                    pathchange: true
                  },
                  scores: {
                    hijack: `+this.ruleForm.hijackscore+`,
                    impact: `+this.ruleForm.impactscore+`,
                    andor: "`+this.ruleForm.andor+`"
                  }
                },
                enabled: true
            )
            {
              response
            }
          }`;
        }
      //  console.log(newrule);
        var newRuleSettings="null";
        try {
          newRuleSettings = await API.graphql(graphqlOperation(newrule));
        } catch(err) {
          console.log(err);
        }
      //  console.log(newRuleSettings);
        var responseMessage = '';
        if (this.addType=='org') {
          responseMessage = newRuleSettings.data.addRulesForOrg;
        }
        if (this.addType=='user') {
          responseMessage = newRuleSettings.data.addRulesForUser;
        }

        // EDIT THIS
      //  console.log(responseMessage.response);

        if (responseMessage.response==null) {
          this.msgColor = "success";
          this.message = responseMessage.response;
        } else {
          this.msgColor = "red";
          this.message = "Error adding Rule " + this.ruleForm.alertname;
        }
      //  console.log(this.addType);
        await this.sleep(1000)
        if (this.addType == "user") {
          this.getRules("user");
        } else {
          this.getRules("org");
        }
        this.shrinkEditWindow();

      } else {
          this.msgColor = "red";
          this.message = "Invalid details: " + invaliddetails;
      }
      this.displayMessage = true;
    },
    toggleRule: async function (rule, scope, mode) {
    //  console.log(mode);
    //  console.log(rule.enabled);
      var newvalue=0;
      if (rule.enabled) {newvalue=1}
      var updatedrule=`mutation toggleRule{ 
        toggleRule(
            id:`+ rule.id +`,
            alertname: "`+rule.alertname+`",
            alertgroup: "`+rule.alertgroup+`",
            alerttype: "`+scope+`",
            enabled: `+newvalue+`
        )
        {
          response
        }
      }`;
      if (updatedrule!=''){
      //  console.log(updatedrule);
        var updateRuleSettings="null";
        try {
          updateRuleSettings = await API.graphql(graphqlOperation(updatedrule));
        } catch(err) {
          console.log(err);
        }
      //  console.log(updateRuleSettings);
        var responseMessage='';
        if (this.addType=='org'){
          responseMessage = updateRuleSettings.data.toggleRule;
        }
        if (this.addType=='user'){
          responseMessage = updateRuleSettings.data.toggleRule;
        }

        // EDIT THIS

        if (responseMessage.response==null) {
          this.msgColor = "success";
          this.message = responseMessage.response;
        } else {
          this.msgColor = "red";
          this.message = "Error updating Rule " + this.ruleForm.alertname;
        }
        await this.sleep(1000)
      //  console.log(scope);
        if (scope == "user") {
          this.getRules("user");
        } else {
          this.getRules("org");
        }
        this.shrinkEditWindow();
      }
      this.displayMessage = true;
    },
    editRule: async function () {
      var valid=true;
      var scope=this.addType;
      var invaliddetails='';
    //  console.log(this.ruleForm);
      if (!this.ruleForm.assertions) {this.ruleForm.assertions_email=false;}
      if (!this.ruleForm.pathchanges) {this.ruleForm.pathchanges_email=false;}
      if (!this.ruleForm.firstseens) {this.ruleForm.firstseens_email=false;}
      var p_in=this.getList(this.ruleForm.prefix_in,true);
      var p_notin=this.getList(this.ruleForm.prefix_notin,true);
      if ((p_in.length==0 && p_notin.length==0) || !this.ruleForm.prefixes_enabled) {
        p_in="";
        p_notin="";
      }
      var o_in=this.getList(this.ruleForm.origins_in,false);
      var o_notin=this.getList(this.ruleForm.origins_notin,false);
      if ((o_in.length==0 && o_notin.length==0) || !this.ruleForm.origins_enabled) {
        o_in="";
        o_notin="";
      }
      var pa_in=this.getList(this.ruleForm.path_contains,false);
      var pa_notin=this.getList(this.ruleForm.path_notcontains,false);
      if ((pa_in.length==0 && pa_notin.length==0) || !this.ruleForm.paths_enabled) {
        pa_in="";
        pa_notin="";
      }
      if (this.ruleForm.alertname.length<3) {
        valid=false;
        invaliddetails+="Rule Name is empty or not long enough (min 3 characters)"
      }
      if (!this.validate_cidrlist(p_in) || !this.validate_cidrlist(p_notin)) {
        valid=false;
        invaliddetails+="Invalid Prefix in prefix list";
      }
      if (!this.validate_asnlist(o_in) || !this.validate_asnlist(o_notin)) {
        valid=false;
        invaliddetails+="Invalid asn in origin list";
      }
      if (!this.validate_asnlist(pa_in) || !this.validate_asnlist(pa_notin)) {
        valid=false;
        invaliddetails+="Invalid asn in path list";
      }
      if (typeof(this.ruleForm.origins_direction) == "object") {
        this.ruleForm.origins_direction=this.ruleForm.origins_direction.value
      }
      if (typeof(this.ruleForm.path_position) == "object") {
        this.ruleForm.path_position=this.ruleForm.path_position.value
      }
      if (typeof(this.ruleForm.andor) == "object") {
        this.ruleForm.andor=this.ruleForm.andor.value
      }
    //  console.log(valid)
      var updatedrule='';
      if (valid) {
    //    console.log(scope);
        var verb=""
        if (scope=='org') {
          verb="updateRulesForOrg"
        } 
        if (scope=="user") {
          verb="updateRulesForUser"
        }
        updatedrule=`mutation updateRules{ 
          `+verb+`(
              id:`+ this.ruleForm.id +`,
              alertname: "`+this.ruleForm.alertname+`",
              alertgroup: "`+this.ruleForm.alertgroup+`",
              alerts: {
                assertion: `+this.ruleForm.assertions+`,
                pathchange: `+this.ruleForm.pathchanges+`,
                firstseen: `+this.ruleForm.firstseens+`
              },
              emails: {
                assertion: `+this.ruleForm.assertions_email+`,
                pathchange: `+this.ruleForm.pathchanges_email+`,
                firstseen: `+this.ruleForm.firstseens_email+`,
                repeat: `+this.ruleForm.repeat+`
              },
              overrides: {
                sethijack: `+this.ruleForm.overrides_hijack+`,
                hijackscore: `+this.ruleForm.overrides_hijack_score+`,
                setimpact: `+this.ruleForm.overrides_impact+`,
                impactscore: `+this.ruleForm.overrides_impact_score+`
              },
              filter: {
                prefix: {
                  isin: `+this.ruleForm.include_subnets+`,
                  is: [`+p_in+`],
                  isnot: [`+p_notin+`]
                },
                origin: {
                  direction: "`+this.ruleForm.origins_direction+`",
                  is: [`+o_in+`],
                  isnot: [`+o_notin+`],
                  filter_asns_in_org: `+this.ruleForm.intraorg+`
                },
                path: {
                  contains: [`+pa_in+`],
                  notcontains: [`+pa_notin+`],
                  position: "`+this.ruleForm.path_position+`",
                  peer: "any",
                  pathchange: true
                },
                scores: {
                  hijack: `+this.ruleForm.hijackscore+`,
                  impact: `+this.ruleForm.impactscore+`,
                  andor: "`+this.ruleForm.andor+`"
                }
              },
              enabled: true
            )
            {
              response
            }
          }`;
      }
      if (updatedrule!=''){
    //    console.log(updatedrule);
        var updateRuleSettings="null";
        try {
          updateRuleSettings = await API.graphql(graphqlOperation(updatedrule));
        } catch(err) {
          console.log(err);
        }
    //    console.log(updateRuleSettings);
        var responseMessage='';
        if (this.addType=='org'){
          responseMessage = updateRuleSettings.data.updateRulesForOrg;
        }
        if (this.addType=='user'){
          responseMessage = updateRuleSettings.data.updateRulesForUser;
        }

        // EDIT THIS

        if (responseMessage.response==null) {
          this.msgColor = "success";
          this.message = responseMessage.response;
        } else {
          this.msgColor = "red";
          this.message = "Error editing Rule " + this.ruleForm.alertname;
        }
        await this.sleep(1000)
    //    console.log(this.addType);
        if (this.addType == "user") {
          this.getRules("user");
        } else {
          this.getRules("org");
        }
        this.shrinkEditWindow();
      } else {
          this.msgColor = "red";
          this.message = "Invalid details: " + invaliddetails;
      }
      this.displayMessage = true;
    },
    getList: function(listString,isStr) {
      var arr=[];
      var csv="";
      var s="";
      for ( s of listString.split(/\r\n|\r|\n/)) {
        s=s.replace(/\s+/g, '');
        if (s.length>0) {
          if (isStr) {
            arr.push(`"`+s+`"`);
          } else {
            arr.push(s);
          }
        }
      }
      csv=arr.join();
      return csv;
    },
    updateDefs: async function () {
      this.ruleForm.hijackscore = this.asnMappings[this.form.type]["h"];
      this.ruleForm.impactscore = this.asnMappings[this.form.type]["i"];
      this.ruleForm.repeat = this.asnMappings[this.form.type]["v"];
    },
    clearMessage: function () {
      this.displayMessage = false;
      this.message = "";
    },
    prepRemoveRule: async function (rule, type) {
      this.clearMessage();
      this.showEdit = false;
      this.addType = type;
      this.deleteRule = rule;
  //    console.log("prepare remove: " + String(rule) + " : " + type);
      this.showDeleteRuleCheck = true;
    },
    removeRule: async function () {
      this.showDeleteRuleCheck = false;
      var remQuery =
        `mutation deleteRule { deleteRule(
                id: ` +
        this.deleteRule.id +
        `
            ) { response } }`;
    //  console.log(remQuery);
      const response = await API.graphql(graphqlOperation(remQuery));
    //  console.log(response);
      var responseMessage = response.data.deleteASN;
      await this.sleep(1000)
      if (!responseMessage) {
        this.msgColor = "success";
        this.message = "Successfully removed Rule " + this.deleteRule.alertname;
      } else {
        this.msgColor = "red";
        this.message = "Error removing Rule " + this.deleteRule.alertname;
      }
      if (this.addType=='org'){
        this.getRules("org");
      } else {
        this.getRules("user");
      }
      this.displayMessage = true;
    },
    shrinkEditWindow: function () {
      this.showEdit = false;
    },
    getAnASNOwner: async function (asn) {
      if (asn == 0) {
        return "None";
      } else {
        try {
          var qry =
            `query getASNOwner { getASNOwner ( asn: "` +
            asn +
            `" ) {
                            organisation
                        }
                    }`;
          //console.log(qry);
          const res = await API.graphql(graphqlOperation(qry));
          var ownData = res.data.getASNOwner;
          //console.log(ownData);
          if (ownData) {
            if (ownData.length > 0) {
              //console.log("owner: ");
              //console.log(ownData[0].organisation);
              if (ownData[0].organisation) {
                return ownData[0].organisation;
              } else {
                return "Unknown";
              }
            } else {
              return "Unknown";
            }
          } else {
            return "Unknown";
          }
        } catch (err) {
          console.log(err);
          return "Unknown";
        }
      }
    },
    getASNOwners: async function () {
      this.is_asn_owners = false;
    //  console.log("gettign asn owners");
      var nero = {};
      for (var i = 0; i < this.asn_get_owner.length; i++) {
        var keys = Object.keys(this.asn_own_dict);
        if (!keys.includes(this.asn_get_owner[i].toString())) {
          nero[this.asn_get_owner[i]] = this.getAnASNOwner(
            this.asn_get_owner[i]
          );
        }
      }
      for (var i = 0; i < this.asn_get_owner.length; i++) {
        //console.log("GETTING AN OWNER")
        if (!keys.includes(this.asn_get_owner[i].toString())) {
          this.asn_own_dict[this.asn_get_owner[i]] = await nero[
            this.asn_get_owner[i]
          ];
        }

        if (i == this.asn_get_owner.length - 1) {
          //console.log("UPDATETICKER")
          this.is_asn_owners = false;
          this.$nextTick(() => {
            // Add the component back in
            this.is_asn_owners = true;
          });
        }
      }
    },
    processRuleData(rule,scope) {
      let p = rule.filter.prefix.is;
      let o = rule.filter.origin.is;
      let pa = rule.filter.path.contains;
      let np = rule.filter.prefix.isnot;
      let no = rule.filter.origin.isnot;
      let npa = rule.filter.path.notcontains;
      rule.ruletype=scope;
      if (rule.enabled==1) { rule.enabled=true } else { rule.enabled=false }
      rule.filter.prefix.isfull = p;
      rule.filter.origin.isfull = o;
      rule.filter.path.containsfull = pa;
      rule.filter.prefix.isnotfull = np;
      rule.filter.origin.isnotfull = no;
      rule.filter.path.notcontainsfull = npa;
      if (p.length > 2) {
        p = p.slice(0, 2);
        let p_more=rule.filter.prefix.is.length-2;
        p.push("(+"+p_more.toString()+" more)");
      }
      if (o.length > 2) {
        o = o.slice(0, 2);
        let o_more=rule.filter.origin.is.length-2;
        o.push("(+"+o_more.toString()+" more)");
      }
      if (pa.length > 2) {
        pa = pa.slice(0, 2);
        let pa_more=rule.filter.path.contains.length-2;
        pa.push("(+"+pa_more.toString()+" more)");
      }

      if (np.length > 2) {
        np = np.slice(0, 2);
        let np_more=rule.filter.prefix.isnot.length-2;
        np.push("(+"+np_more.toString()+" more)");
      }
      if (no.length > 2) {
        no = no.slice(0, 2);
        let no_more=rule.filter.origin.isnot.length-2;
        no.push("(+"+no_more.toString()+" more)");
      }
      if (npa.length > 2) {
        npa = npa.slice(0, 2);
        let npa_more=rule.filter.path.notcontains.length-2;
        npa.push("(+"+npa_more.toString()+" more)");
      }
      rule.filter.prefix.is = p;
      rule.filter.origin.is = o;
      rule.filter.path.contains = pa;
      rule.filter.prefix.isnot = np;
      rule.filter.origin.isnot = no;
      rule.filter.path.notcontains = npa;
      return rule;
    },
    getRules: async function (scope) {
      this.loading = true;
      this.organisation_download_rules = [];
      var verb=""
      if (scope=="user") {
        verb="getRulesForUser"
        this.user_download_rules = [];
      } else {
        verb="getRulesForOrg"
        this.organisation_download_rules = [];
      }
      var ruleQuery =
        `query `+verb+` {
                `+verb+`(start_time: ` +
        Math.floor(Date.now() / 1000 - 604800) +
        `) {
                    id
                    alertgroup
                    alertname
                    overrides {
                    highjackscore
                    impactscore
                    sethijack
                    setimpact
                    }
                    alerts {
                    assertion
                    firstseen
                    pathchange
                    }
                    emails {
                    assertion
                    firstseen
                    pathchange
                    repeat
                    }
                    filter {
                    origin {
                        direction
                        filter_asns_in_org
                        is
                        isnot
                    }
                    path {
                        as_in_path
                        contains
                        notcontains
                        pathchange
                        peer
                        position
                    }
                    prefix {
                        is
                        isin
                        isnot
                    }
                    scores {
                        andor
                        hijack
                        impact
                    }
                    }
                    notification_count
                    enabled
                }
            }
            `;

      var rules = [];
    //  console.log(ruleQuery);
      const ruleData = await API.graphql(graphqlOperation(ruleQuery));
    //  console.log(ruleData);
      this.ruleGroups=['Default']
      if (scope=='org'){
        if (ruleData.data.getRulesForOrg.length == 0 || rules === undefined) {
          this.orgRules = [];
        } else {
          for (var r in Object.entries(ruleData.data.getRulesForOrg)) {
            let rule = this.processRuleData(ruleData.data.getRulesForOrg[r],"org") ;
            rules.push(rule);
            if (rule.alertgroup.length>0) {
              if (this.ruleGroups.indexOf(rule.alertgroup)<0) {
                this.ruleGroups.push(rule.alertgroup)
              }
            }

          }
      //    console.log(rules);
          this.orgRules = rules;
        }
      }
      if (scope=='user'){
        if (ruleData.data.getRulesForUser.length == 0 || rules === undefined) {
          this.userRules = [];
        } else {
          for (var r in Object.entries(ruleData.data.getRulesForUser)) {
            let rule = this.processRuleData(ruleData.data.getRulesForUser[r],"user") ;
            rules.push(rule);
            if (rule.alertgroup.length>0) {
              if (this.ruleGroups.indexOf(rule.alertgroup)<0) {
                this.ruleGroups.push(rule.alertgroup)
              }
            }

          }
      //    console.log(rules);
          this.userRules = rules;
        }
      }
      var i;
      var download_rules=[];
      for (i = 0; i < rules.length; i++) {
        download_rules.push({
          'alertname': rules[i].alertname,
          'alertgroup': rules[i].alertgroup,
          'alerttype': scope,
          'assertion': rules[i].alerts.assertion? 'Y' : 'N',
          'firstseen': rules[i].alerts.firstseen? 'Y' : 'N',
          'pathchange': rules[i].alerts.pathchange? 'Y' : 'N',
          'assertion_emails': rules[i].emails.assertion? 'Y' : 'N',
          'firstseen_emails': rules[i].emails.firstseen? 'Y' : 'N',
          'pathchange_emails': rules[i].emails.pathchange? 'Y' : 'N',
          'repeat_emails': rules[i].emails.repeat,
          'origin_direction': rules[i].filter.origin.direction,
          'origin_filter_asns_in_org': rules[i].filter.origin.filter_asns_in_org? 'Y' : 'N',
          'origin_is': rules[i].filter.origin.isfull,
          'origin_isnot': rules[i].filter.origin.isnotfull,
          'path_as_in_path': rules[i].filter.path.as_in_path? 'Y' : 'N',
          'path_as_contains': rules[i].filter.path.containsfull,
          'path_as_notcontains': rules[i].filter.path.notcontainsfull,
          'path_as_position': rules[i].filter.path.postition,
          'prefix_is': rules[i].filter.prefix.isfull,
          'prefix_isin': rules[i].filter.prefix.isin? 'Y' : 'N',
          'prefix_isnot': rules[i].filter.prefix.isnotfull,
          'scores_hijack': rules[i].filter.scores.hijack,
          'scores_impact': rules[i].filter.scores.impact,
          'scores_andor': rules[i].filter.scores.andor,
          'over_seth': rules[i].overrides.sethijack? 'Y' : 'N',
          'over_hscore': rules[i].overrides.hijackscore,
          'over_seti': rules[i].overrides.sethimpact? 'Y' : 'N',
          'over_iscore': rules[i].overrides.impactscore,
          'enabled': rules[i].enabled
        })
      }
      if (scope=="user") {
        this.user_download_rules=download_rules;
      } else {
        this.organisation_download_rules=download_rules;
      }
      this.loading = false;
    },
  },
  watch: {
    showAdvanced(n) {
  //    console.log("setting show advanced settings");
  //    console.log(n);
      this.$store.commit("setShowSettings", { advancedSettings: n });
  //    console.log(this.$store.state.showSettings);
    },
  },
};
</script>

<style scoped>
table.v-table tbody td:first-child,
table.v-table tbody td:not(:first-child),
table.v-table tbody th:first-child,
table.v-table tbody th:not(:first-child),
table.v-table thead td:first-child,
table.v-table thead td:not(:first-child),
table.v-table thead th:first-child,
table.v-table thead th:not(:first-child) {
  padding: 0 5px;
}

table.v-table tbody td,
table.v-table tbody th {
  height: 38px;
}

tbody tr:nth-of-type(even) {
  background-color: rgba(0, 0, 0, 0.05);
}

.v-table tr:hover:not(.v-table__expanded__content) {
  background-color: rgba(0, 0, 0, 0.4);
}

.switch-center {
  display: flex;
  justify-content: center;
}
</style>
